import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography,
    Box,
    Card,
    Divider,
    Chip,
    IconButton,
    InputAdornment,
    Tooltip,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TablePagination,
    CircularProgress
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';

// Styles
import './SchoolSetup.css';
import StandardPageWrapper from '../../../components/StandardPageWrapper/StandardPageWrapper';

const STEPS = [
    'Organization Info',
    'Branding Info',
    'Card Config',
    'Front Side Fields',
    'Back Side Fields',
    'Lanyard Config',
    'Design Preferences'
];

// Pre-seeded school list for the submission form
const REGISTERED_SCHOOLS = [
    "ABC Public School",
    "Greenfield Academy",
    "International Tech Institute",
    "Delta High School",
    "St. Mary's School",
    "Zenith Management College"
];

const MARATHI_SIGN_TAGS = [
    "मुख्याध्यापक",
    "प्राचार्य",
    "अध्यक्ष",
    "सचिव",
    "स्वाक्षरी",
    "कर्मचारी स्वाक्षरी"
];

const ENGLISH_SIGN_TAGS = [
    "Principal",
    "H.M.",
    "HEAD MASTER",
    "DIRECTOR",
    "SECRETARY",
    "PRECIDENT",
    "HOD",
    "CHAIRMAN",
    "MANAGER",
    "AUTH. SIGN.",
    "AUTHORISED SIGNATURE",
    "AUTHORITY SIGNATURE"
];

const DESIGN_COLOR_MAP = {
    'Red': { primary: '#D32F2F', secondary: '#FFEBEE' },
    'Blue': { primary: '#1976D2', secondary: '#E3F2FD' },
    'Royal Blue': { primary: '#1A237E', secondary: '#E8EAF6' },
    'Brown': { primary: '#5D4037', secondary: '#EFEBE9' },
    'Yellow': { primary: '#FBC02D', secondary: '#FFFDE7' },
    'Orange': { primary: '#F57C00', secondary: '#FFF3E0' },
    'Green': { primary: '#388E3C', secondary: '#E8F5E9' }
};

// Initial mock data to seed localStorage if empty
const DEFAULT_SUBMISSIONS = [
    {
        organizationDetails: {
            name: "ABC Public School",
            type: "School",
            address: "123 Main Street, Sector 4",
            city: "Mumbai",
            state: "Maharashtra",
            country: "India",
            pincode: "400001",
            contactNumber: "9876543210",
            alternateContactNumber: "022-2567890",
            email: "contact@abcpublicschool.edu",
            website: "www.abcpublicschool.edu",
            additionalInfo: "Affiliated with CBSE Board."
        },
        brandingDetails: {
            logo: "abc_school_logo.png",
            authoritySignature: "trustee_signature.png",
            principalSignature: "principal_signature.png",
            stamp: "official_school_stamp.png",
            primaryColor: "#1e3a8a",
            secondaryColor: "#bfdbfe",
            accentColor: "#f59e0b",
            fontPreference: "Outfit"
        },
        cardConfiguration: {
            cardType: "Student ID Card",
            orientation: "Vertical",
            cardSize: "88 mm × 56 mm (Default)",
            customCardSize: "",
            generateLanyard: "Yes"
        },
        frontFields: ["Student Name", "Roll Number", "Class", "Division", "Blood Group", "Student Photo"],
        frontCustomFields: ["Scholar Number", "Medical Condition"],
        backFields: ["Address", "Parent Mobile Number", "Valid Upto", "Instructions"],
        backCustomFields: [],
        lanyardConfiguration: {
            brandName: "ABC PUBLIC SCHOOL",
            logo: "abc_school_logo.png",
            primaryColor: "#1e3a8a",
            secondaryColor: "#bfdbfe",
            lanyardText: "Organization Name",
            customText: ""
        },
        designPreferences: {
            theme: "Premium",
            colorStyle: "Gradient",
            notes: "Use royal blue theme. Make student name larger. Keep photo in center."
        },
        dateSubmitted: "2026-06-30"
    },
    {
        organizationDetails: {
            name: "Delta High School",
            type: "School",
            address: "789 Pine Road, Bypass Road",
            city: "Pune",
            state: "Maharashtra",
            country: "India",
            pincode: "411001",
            contactNumber: "9988776655",
            alternateContactNumber: "",
            email: "info@deltahigh.edu",
            website: "www.deltahigh.edu",
            additionalInfo: "State Board Syllabus."
        },
        brandingDetails: {
            logo: "delta_logo.png",
            authoritySignature: null,
            principalSignature: "principal_sign.png",
            stamp: null,
            primaryColor: "#065f46",
            secondaryColor: "#a7f3d0",
            accentColor: "#fbbf24",
            fontPreference: "Roboto"
        },
        cardConfiguration: {
            cardType: "Employee ID Card",
            orientation: "Horizontal",
            cardSize: "CR80 Standard",
            customCardSize: "",
            generateLanyard: "No"
        },
        frontFields: ["Employee Name", "Employee ID", "Designation", "Employee Code", "Joining Date"],
        frontCustomFields: ["RFID Tag"],
        backFields: ["Address", "Mobile Number", "Emergency Contact Number", "Terms & Conditions"],
        backCustomFields: [],
        lanyardConfiguration: null,
        designPreferences: {
            theme: "Corporate",
            colorStyle: "Solid Color",
            notes: "Keep it simple and neat. Use dark green color code for header banner."
        },
        dateSubmitted: "2026-06-29"
    }
];

const FRONT_FIELD_CATEGORIES = {
    "Basic Information": [
        "Student Name", "Employee Name", "Student ID", "Employee ID",
        "Admission Number", "Roll Number", "GR Number",
        "Registration Number", "PRN Number", "PEN Number", "APAAR ID"
    ],
    "Academic Information": [
        "Class", "Division", "Section", "Standard", "Department",
        "Course", "Stream", "Semester", "Batch", "Academic Year"
    ],
    "Personal Information": [
        "Date of Birth", "Gender", "Blood Group", "Nationality", "Religion", "Category"
    ],
    "Contact Information": [
        "Mobile Number", "Parent Mobile Number", "Email Address", "Address", "Emergency Contact Number"
    ],
    "Transportation Information": [
        "Bus Route", "Bus Stop", "Vehicle Number"
    ],
    "Employment Information": [
        "Designation", "Joining Date", "Employee Code", "Office Department"
    ],
    "Guardian Information": [
        "Father Name", "Mother Name", "Guardian Name", "Parent Occupation"
    ],
    "Images": [
        "Student Photo", "Father Photo", "Mother Photo"
    ],
    "Other Information": [
        "Valid Upto", "Issue Date", "House Name", "Hostel Name", "Library Number"
    ]
};

const BACK_FIELD_CATEGORIES = {
    "Common Fields": [
        "Date of Birth", "Blood Group", "Mobile Number", "Parent Mobile Number",
        "Address", "Emergency Contact Number", "Aadhaar Number", "APAAR ID",
        "PEN Number", "Bus Route", "Bus Stop", "Valid Upto", "Issue Date"
    ],
    "Additional Information": [
        "Instructions", "Terms & Conditions", "Emergency Notes", "School Timings", "Transport Information"
    ],
    "Images": [
        "Father Photo", "Mother Photo"
    ]
};

const ENGLISH_CLASSES_REGULAR = [
    'Play Group', 'Nursery', 'Jr.Kg.', 'Sr.Kg.', 'LKG', 'UKG', 'KG', 'PRE',
    '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th',
    '11th - Art\'s', '11th - Commerce', '11th - Science',
    '12th - Art\'s', '12th - Commerce', '12th - Science'
];

const ENGLISH_CLASSES_ROMAN = [
    'Play Group', 'Nursery', 'Jr.Kg.', 'Sr.Kg.', 'LKG', 'UKG', 'KG', 'PRE',
    'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII',
    'XI - Art\'s', 'XI - Commerce', 'XI - Science',
    'XII - Art\'s', 'XII - Commerce', 'XII - Science'
];

const MARATHI_CLASSES = [
    '१ ली', '२ री', '३ री', '४ थी', '५ वी', '६ वी', '७ वी', '८ वी', '९ वी', '१० वी', '११ वी', '१२ वी',
    '११ वी - कला', '११ वी - वाणिज्य', '११ वी - विज्ञान',
    '१२ वी - कला', '१२ वी - वाणिज्य', '१२ वी - विज्ञान'
];

const ENGLISH_DIVISIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const MARATHI_DIVISIONS = ['अ', 'ब', 'क', 'ड', 'इ'];

const HOUSE_OPTIONS = ['Red', 'Green', 'Yellow', 'Blue', 'All'];

const FIELDS_ENGLISH_STUDENT = [
    'STUDENT NAME', 'CLASS', 'STD.', 'GRADE', 'Mother Name', 'Branch', 'Trade',
    'ROLL NO.', 'GR.NO.', 'DATE OF BIRTH', 'PEN NO.', 'APAAR ID', 'STUDENT ID',
    'AADHAR NO.', 'MOBILE NO.', 'CONTACT NO.', 'EMER. CONTACT NO.', 'PRN. NO.',
    'REG. NO.', 'Blood Group', 'ADDRESS', 'Mother Photo Image', 'Father Photo Image'
];

const FIELDS_ENGLISH_STAFF = [
    'EMPLOYEE NAME', 'DESIGNATION', 'DEPERTMENT', 'QUALIFICATION', 'Blood Group',
    'DATE OF BIRTH', 'DATE OF JOINING', 'SHALARTH ID', 'AADHAR NO.', 'PAN NO.',
    'MOBILE NO.', 'EMAIL ID', 'ADDRESS'
];

const FIELDS_MARATHI_STUDENT = [
    'विद्यार्थ्याचे नाव', 'वर्ग', 'इयत्ता', 'आईचे नाव', 'ज. रजि. न.', 'जन्म दिनांक',
    'PEN No.', 'APAAR ID', 'STUDENT ID', 'आधार नं.', 'मो. नंबर', 'रक्तगट', 'पत्ता'
];

const FIELDS_MARATHI_STAFF = [
    'कर्मचाऱ्यांचे नाव', 'पद', 'शिक्षण', 'रक्तगट', 'जन्म दिनांक', 'रुजू दिनांक',
    'शालार्थ क्र.', 'सेवार्थ क्र.', 'नॅशनल टीचर कोड', 'आधार नं.', 'पॅन नंबर', 'मो. नंबर',
    'ई-मेल', 'पत्ता'
];

const DEFAULT_FRONT_TEMPLATE = `# MASTER PROMPT – UNIVERSAL PREMIUM ID CARD FRONT SIDE GENERATOR

Create a PREMIUM PROFESSIONAL ID CARD DESIGN (FRONT SIDE ONLY) for commercial printing using the uploaded logo, organization details, card holder photo, and user-provided information.
• Orientation: {{orientation}}

Suitable For:

• School
• College
• Coaching Institute
• Company
• Hospital
• NGO
• Government Office
• Training Institute
• Business Organization
• Any Custom Institution

━━━━━━━━━━━━━━━━━━
CARD SPECIFICATIONS
━━━━━━━━━━━━━━━━━━

• Orientation: {{orientation}}
• Exact Size: {{cardSize}} (Mandatory)
• Safe Margin: 5 mm inside all sides
• Resolution: 300 DPI
• Color Mode: CMYK
• Print Ready
• Clean Vector Artwork
• Premium Branding
• Modern Corporate Style
• No Rounded Corners
• No Lanyard Slot
• No Watermark
• No QR Code
• No Barcode

━━━━━━━━━━━━━━━━━━
LOGO & BRANDING RULES
━━━━━━━━━━━━━━━━━━

• Place Organization Logo at the TOP CENTER of the card
• Use uploaded logo only
• Improve logo sharpness and quality
• Convert logo into clean vector-style appearance
• Maintain original proportions
• Do not stretch or distort logo
• Automatically extract colors from logo if color theme is not specified
• Maintain strong brand identity throughout the design
• Use logo colors in header, footer, borders, gradients, icons, and decorative elements

━━━━━━━━━━━━━━━━━━
HEADER SECTION
━━━━━━━━━━━━━━━━━━

Display at Top:

[ORGANIZATION LOGO]

{{schoolName}} (Render in bold text - do not use any border around organization name)

[ORGANIZATION ADDRESS]

[ADDITIONAL INFORMATION IF PROVIDED]

Header Style:

• Premium Institutional Branding
• Strong Visual Hierarchy
• Elegant Alignment
• Professional Typography
• National-Level Branding Quality
• Clean Corporate Appearance

━━━━━━━━━━━━━━━━━━
PHOTO SECTION
━━━━━━━━━━━━━━━━━━

Display Card Holder Photo prominently.

Requirements:

• Large Professional Photo Area
• Rounded Rectangle or Premium Frame
• Clean White Border
• Sharp Photo Presentation
• Professional Alignment
• Premium Photo Placement
• Balanced Spacing Around Photo

━━━━━━━━━━━━━━━━━━
INFORMATION SECTION
━━━━━━━━━━━━━━━━━━

Display ONLY user-provided fields.

Rules:
• Student Name Display Only Value
• Other field do not display any icon and Show Field Name + Value in center
• Do not create extra fields
• Do not display fields not provided by the user
• Use premium icons where suitable
• Maintain perfect alignment
• High readability
• Professional typography hierarchy
• Equal spacing between fields
• Modern information layout

Example:

Name: Rahul Sharma
Class: VIII-A
Roll No.: 25
Blood Group: O+
Mobile No.: 9876543210

Use only fields provided by the user.

━━━━━━━━━━━━━━━━━━
SIGNATURE SECTION
━━━━━━━━━━━━━━━━━━

Display at Bottom:

[AUTHORITY SIGNATURE]

{{authorityDesignation}}

Requirements:

• Use uploaded signature only
• Improve signature clarity
• Transparent clean signature appearance
• Professional signature placement
• Signature should appear authentic and official
• Maintain sufficient spacing from information section

━━━━━━━━━━━━━━━━━━
DESIGN STYLE
━━━━━━━━━━━━━━━━━━

Create a premium modern identity card front side featuring:

• National-Level Branding Quality
• Premium Educational / Corporate Identity
• Modern Corporate Layout
• Elegant Geometric Elements
• Premium Gradient Effects
• Sophisticated Decorative Shapes
• Professional Information Panel
• Premium Typography Hierarchy
• Sharp Vector Graphics
• Excellent Readability
• Clean White Space Management
• Adobe Illustrator Quality Design

━━━━━━━━━━━━━━━━━━
BACKGROUND ELEMENTS
━━━━━━━━━━━━━━━━━━

Use subtle premium design elements:
• Create Creative professional Design
• Academic / Corporate Graphics
• Modern Geometric Shapes
• Soft Gradient Overlays
• Premium Decorative Elements
• Abstract Branding Elements
• Modern Vector Graphics
• Clean Professional Textures
• Non-Distracting Background
• Adobe Illustrator Quality Design

━━━━━━━━━━━━━━━━━━
QUALITY REQUIREMENTS
━━━━━━━━━━━━━━━━━━

• National-Level Branding Quality
• Adobe Illustrator Quality
• Publishing House Quality
• Commercial Printing Quality
• Premium Typography
• Professional Vector Artwork
• Attractive Modern Design
• Production-Ready Layout

━━━━━━━━━━━━━━━━━━━━ DESIGN REQUIREMENTS ━━━━━━━━━━━━━━━
• Premium Educational Branding • Modern School Identity Design • Corporate-Quality Layout • Professional
Typography Hierarchy • Elegant Geometric Design Elements • Smooth Modern Gradients • Sharp Vector Graphics
• Balanced Composition • Clean White Space Management • High Readability • Realistic Print-Ready Appearance •
Production Quality Artwork

━━━━━━━━━━━━━━━━━━
FINAL OUTPUT REQUIREMENTS
━━━━━━━━━━━━━━━━━━

• FRONT SIDE ONLY
• Orientation: {{orientation}}
• Exact Size: {{cardSize}}
• Maintain 7 mm Safe Margin Top and Bottom side
• Maintain 5 mm Safe Margin Left and Right side
• CMYK Commercial Print Ready
• Premium Branding
• Modern Corporate Design
• Organization Information at Top
• Card Holder Photo
• User Information Fields Only
• Authority Signature at Bottom
• Professional Visual Hierarchy
• Sharp Vector Graphics
• Premium Typography
• Commercial Printing Quality
• Realistic Presentation Mockup
• No QR Code
• No Barcode
• No Rounded Corners
• No Lanyard Hole
• No Watermark
• Adobe Illustrator Quality Artwork
• Production-Ready Design
• Create Creative professional Design

• Premium Educational Branding • Modern School Identity Design • Corporate-Quality Layout • Professional
Typography Hierarchy • Elegant Geometric Design Elements • Smooth Modern Gradients • Sharp Vector Graphics
• Balanced Composition • Clean White Space Management • High Readability • Realistic Print-Ready Appearance •
Production Quality Artwork

• Automatically adapt to any logo, organization name, address, color theme, card holder photo,
authority signature, and user-provided information fields
Use only given field or information
Do not use extra field, icon or information
Student name in plan bold text do not use any border in school name
Do not use authorized signature text
If Authorised Sign Tag is there align it
Do not remove photo border
Maintain 5mm safe margin from top and bottom side
Use canvas full area
Do not Display White outline of design
• Ultra-Clean Premium Front Side ID Card Design
• Orientation: {{orientation}}
• Exact Size: {{cardSize}}
do not print outline

Color: Use logo color`;

const DEFAULT_BACK_TEMPLATE = `# MASTER PROMPT – UNIVERSAL PREMIUM ID CARD BACK SIDE GENERATOR (ICONS ONLY)

Create a PREMIUM PROFESSIONAL ID CARD DESIGN (BACK SIDE ONLY) for commercial printing using the uploaded logo and user-provided organization details.
• Orientation: {{orientation}}

Suitable For:

• School
• College
• Coaching Institute
• Company
• Hospital
• NGO
• Government Office
• Training Institute
• Business Organization
• Any Custom Institution

━━━━━━━━━━━━━━━━━━
CARD SPECIFICATIONS
━━━━━━━━━━━━━━━━━━

• Orientation: {{orientation}}
• Exact Size: {{cardSize}} (Mandatory)
• Safe Margin: 5 mm inside all sides
• Resolution: 300 DPI
• Color Mode: CMYK
• Print Ready
• Clean Vector Artwork
• Premium Branding
• Modern Corporate Style
• No Rounded Corners
• No Lanyard Slot
• No Watermark
• No QR Code
• No Barcode

━━━━━━━━━━━━━━━━━━
LOGO & BRANDING RULES
━━━━━━━━━━━━━━━━━━

• Use uploaded logo only
• Improve logo sharpness and quality
• Convert logo into clean vector-style appearance
• Maintain original proportions
• Do not stretch or distort logo
• Automatically extract colors from logo if color theme is not specified
• Maintain strong brand identity throughout the design
• Use logo colors in header, footer, icons, gradients, and decorative elements

━━━━━━━━━━━━━━━━━━
BACK SIDE DESIGN STYLE
━━━━━━━━━━━━━━━━━━

Create a premium modern identity card back side featuring:

• National-Level Branding Quality
• Premium Educational / Corporate Identity
• Modern Corporate Layout
• Elegant Geometric Elements
• Soft Professional Gradients
• Premium Decorative Shapes
• Sophisticated Information Panel
• Professional Typography Hierarchy
• Sharp Vector Graphics
• Excellent Readability
• Clean White Space Management

━━━━━━━━━━━━━━━━━━
INFORMATION SECTION
━━━━━━━━━━━━━━━━━━

IMPORTANT RULES

• Do NOT display any field names.
• Do NOT display any section headings.
• Do NOT display labels such as:
  - Roll No.
  - GR. No.
  - Admission No.
  - Student ID
  - Employee ID
  - PRN No.
  - PEN No.
  - APAAR ID
  - Aadhaar No.
  - Blood Group
  - Date of Birth
  - Mobile No.
  - Parent Mobile
  - Address
  - Any custom field names

• Automatically assign a suitable premium icon for each user-provided field.
• Display ONLY icon + dummy value.
• Display icon on the left.
• Display dummy value on the right.
• Use bold premium typography.
• Maintain equal spacing and alignment.
• Keep maximum readability.
• Leave sufficient blank space beside information.
• Maintain a clean premium appearance.

Generate realistic dummy values according to field type.

Examples:

[Blood Group Icon]       O+

[Calendar Icon]          15-08-2015

[ID Card Icon]           25

[Mobile Icon]            9876543210

[Location Icon]          Sample Area, Sample City

[PEN Icon]               PEN123456789

[APAAR Icon]             1234 5678 9012

[Aadhaar Icon]           1234 5678 9012

For custom fields automatically generate suitable demonstration values.

IMPORTANT:

• Show Icons + Values Only.
• Never display field labels.
• Never display field names.
• Automatically adapt icons according to field type.

━━━━━━━━━━━━━━━━━━
FOOTER SECTION
━━━━━━━━━━━━━━━━━━

Create a premium branded footer.

Display:

[ORGANIZATION NAME]

[ORGANIZATION ADDRESS]

[ADDITIONAL INFORMATION IF PROVIDED]

Footer Style:

• Premium Footer Band
• Elegant Dividers
• Professional Alignment
• Strong Brand Presence
• High Readability
• Corporate Identity Finish

━━━━━━━━━━━━━━━━━━
BACKGROUND ELEMENTS
━━━━━━━━━━━━━━━━━━

Use subtle premium design elements:
• Create Creative professional Design
• Academic / Corporate Graphics
• Modern Geometric Shapes
• Soft Gradient Overlays
• Premium Decorative Elements
• Abstract Branding Elements
• Modern Vector Graphics
• Clean Professional Textures
• Non-Distracting Background

━━━━━━━━━━━━━━━━━━
QUALITY REQUIREMENTS
━━━━━━━━━━━━━━━━━━

• National-Level Branding Quality
• Adobe Illustrator Quality
• Publishing House Quality
• Commercial Printing Quality
• Premium Typography
• Professional Vector Artwork
• Attractive Modern Design
• Production-Ready Layout

━━━━━━━━━━━━━━━━━━
FINAL OUTPUT REQUIREMENTS
━━━━━━━━━━━━━━━━━━

• BACK SIDE ONLY
• Orientation: {{orientation}}
• Exact Size: {{cardSize}}
• Maintain 7 mm Safe Margin Top and Bottom side
• Maintain 5 mm Safe Margin Left and Right side
• CMYK Commercial Print Ready
• Premium Branding
• Modern Corporate Design
• Professional Visual Hierarchy
• Sharp Vector Graphics
• Premium Typography
• Commercial Printing Quality
• Realistic Presentation Mockup
• No QR Code
• No Barcode
• No Rounded Corners
• No Lanyard Hole
• No Watermark
• Adobe Illustrator Quality Artwork
• Production-Ready Design
• Creative and professional Design
• Premium Educational Branding • Modern School Identity Design • Corporate-Quality Layout • Professional Typography Hierarchy • Elegant Geometric Design Elements • Smooth Modern Gradients • Sharp Vector Graphics • Balanced Composition • Clean White Space Management • High Readability • Realistic Print-Ready Appearance • Production Quality Artwork

• Automatically adapt to any logo, organization name, address, color theme, and user-provided information fields
• Icons + Values Only
• No Field Labels
• No Information Headings
Use only given field or information 
Do not use extra field or information 
Maintain 5mm safe margin from top and bottom side
Use canvas full area 
DO not Display White outline of design
• Ultra-Clean Premium Front Side ID Card Design
Maintain size 88mm x 56mm 
Match color, design and organisations information with front side if information is not given 
• Ultra-Clean Premium Back Side ID Card Design

COLOR: 

• Orientation: {{orientation}}
• Exact Size: {{cardSize}}
do not print outline`;

const DEFAULT_LANYARD_TEMPLATE = `# MASTER PROMPT – UNIVERSAL PREMIUM LANYARD DESIGN GENERATOR

Create a PREMIUM, MODERN, CREATIVE, and PROFESSIONAL single-sided lanyard design suitable for commercial printing using the uploaded logo and brand information.

━━━━━━━━━━━━━━━━━━
OUTPUT REQUIREMENTS
━━━━━━━━━━━━━━━━━━

• Output Type: Single Lanyard Design
• Orientation: Landscape
• Canvas Ratio: 16:9 Preview
• Actual Print Size: 406 mm × 19 mm
• Resolution: 300 DPI
• Color Mode: CMYK
• Bleed: 3 mm
• Print-Ready Flat Artwork Only
• No mockup
• No buckle
• No hook
• No ID card
• No fabric texture
• No shadows
• No perspective view
• No folded view
• No watermark

━━━━━━━━━━━━━━━━━━
CANVAS & PRINT SPECIFICATIONS
━━━━━━━━━━━━━━━━━━

• Finished Artwork Size:
  406 mm × 19 mm

• Canvas Preview:
  Landscape (16:9)

• Resolution:
  300 DPI

• Color Mode:
  CMYK

• Bleed:
  3 mm

━━━━━━━━━━━━━━━━━━
LAYOUT RULES
━━━━━━━━━━━━━━━━━━

1. The artwork must be a single continuous lanyard strip.

2. The complete design must start with the Logo.

3. The complete design must end with the Brand Name.

4. Repeat the following pattern across the entire lanyard length:

   Logo → Brand Name → Logo → Brand Name → Logo → Brand Name

5. The sequence must continue seamlessly from one end to the other.

━━━━━━━━━━━━━━━━━━
LOGO & BRAND NAME RULES
━━━━━━━━━━━━━━━━━━

• Position both logo and brand name in the vertical center of the lanyard.

• Logo and brand name together should occupy approximately **30% of the lanyard height**, leaving generous space above and below for a clean, premium appearance.

• The visual height of the logo and the brand name must be equal.

• Maintain the logo's original proportions.

• Never stretch, compress, rotate, distort, crop, or modify the logo.

• Brand name must always appear in a single straight horizontal line.

• Do not wrap text.

• Do not stack words.

• Do not curve the text.

• Use a bold, modern, highly readable professional font.

━━━━━━━━━━━━━━━━━━
SPACING RULES
━━━━━━━━━━━━━━━━━━

• Keep approximately **1 inch safe margin** on both the left and right ends.

• Equal spacing between every logo and brand name.

• Equal spacing from top and bottom edges.

• Maintain a clean, balanced rhythm throughout the strip.

━━━━━━━━━━━━━━━━━━
BACKGROUND DESIGN
━━━━━━━━━━━━━━━━━━

Create a premium background using a combination of:

• Smooth gradients
• Abstract shapes
• Geometric patterns
• Dynamic lines
• Curves
• Waves
• Modern corporate elements
• Minimal premium textures

The background should enhance the overall appearance without overpowering the logo or brand name.

━━━━━━━━━━━━━━━━━━
COLOR RULES
━━━━━━━━━━━━━━━━━━

• Automatically extract the primary colors from the uploaded logo.

• Use harmonious supporting colors when needed.

• Maintain strong contrast between the background and the logo/text.

• Ensure a premium, elegant, and professional appearance.

━━━━━━━━━━━━━━━━━━
PRINT QUALITY
━━━━━━━━━━━━━━━━━━

• Commercial printing quality

• Sharp vector-style graphics

• Clean typography

• Crisp edges

• High readability

• No pixelation

• No unnecessary decorative objects

• No placeholder graphics

━━━━━━━━━━━━━━━━━━
SUITABLE FOR
━━━━━━━━━━━━━━━━━━

• Schools
• Colleges
• Universities
• Coaching Institutes
• Government Offices
• Corporate Companies
• Hospitals
• NGOs
• Events
• Industrial Organizations
• Training Institutes
• Any Professional Brand

━━━━━━━━━━━━━━━━━━
FINAL OUTPUT
━━━━━━━━━━━━━━━━━━

Generate **one premium single-sided lanyard design** that satisfies all of the following:

✓ 406 mm × 19 mm
✓ Landscape preview (16:9)
✓ CMYK
✓ 300 DPI
✓ Flat print-ready artwork
✓ Starts with Logo
✓ Ends with Brand Name
✓ Repeating pattern: Logo → Brand Name
✓ Logo and brand name centered vertically
✓ Logo + brand name occupy approximately 30% of the lanyard height
✓ Logo and brand name have equal visual height
✓ Brand name remains in a single straight line
✓ Equal spacing throughout the design
✓ Premium, modern, creative, elegant, and production-ready.
Brand name is single straight line 
Reduce logo size 
Increase brand name height 
Use Two logo and one brand name in center
Create creative professional background`;

const PLACEHOLDERS = [
    { label: 'School Name', value: '{{schoolName}}' },
    { label: 'School Type', value: '{{schoolType}}' },
    { label: 'Address', value: '{{address}}' },
    { label: 'City', value: '{{city}}' },
    { label: 'State', value: '{{state}}' },
    { label: 'Country', value: '{{country}}' },
    { label: 'Pincode', value: '{{pincode}}' },
    { label: 'Contact No', value: '{{contactNumber}}' },
    { label: 'Email', value: '{{email}}' },
    { label: 'Website', value: '{{website}}' },
    { label: 'Card Category', value: '{{cardType}}' },
    { label: 'Orientation', value: '{{orientation}}' },
    { label: 'Card Size', value: '{{cardSize}}' },
    { label: 'Primary Color', value: '{{primaryColor}}' },
    { label: 'Secondary Color', value: '{{secondaryColor}}' },
    { label: 'Accent Color', value: '{{accentColor}}' },
    { label: 'Color 4', value: '{{color4}}' },
    { label: 'Color 5', value: '{{color5}}' },
    { label: 'Logo Asset', value: '{{logo}}' },
    { label: 'Authority Sign', value: '{{authoritySignature}}' },
    { label: 'Authority Designation', value: '{{authorityDesignation}}' },
    { label: 'Principal Sign', value: '{{principalSignature}}' },
    { label: 'School Stamp', value: '{{stamp}}' },
    { label: 'Front Fields', value: '{{frontFields}}' },
    { label: 'Back Fields', value: '{{backFields}}' },
    { label: 'Lanyard Name', value: '{{lanyardBrandName}}' },
    { label: 'Lanyard Logo', value: '{{lanyardLogo}}' },
    { label: 'Lanyard Primary', value: '{{lanyardPrimaryColor}}' },
    { label: 'Lanyard Secondary', value: '{{lanyardSecondaryColor}}' },
    { label: 'Lanyard Text', value: '{{lanyardText}}' },
    { label: 'Theme Style', value: '{{designTheme}}' },
    { label: 'Background Style', value: '{{colorStyle}}' },
    { label: 'Special Notes', value: '{{specialNotes}}' }
];

const compileTemplate = (template, config, side) => {
    if (!config) return '';
    const {
        organizationDetails = {},
        brandingDetails = {},
        cardConfiguration = {},
        frontFields = [],
        frontCustomFields = [],
        backFields = [],
        backCustomFields = [],
        lanyardConfiguration = null,
        designPreferences = {}
    } = config;

    let compiled = template;

    // Resolve any remaining placeholders inside the template instructions
    compiled = compiled.replace(/\{\{schoolName\}\}/g, organizationDetails.name || 'N/A');
    compiled = compiled.replace(/\{\{schoolType\}\}/g, organizationDetails.type || 'N/A');
    compiled = compiled.replace(/\{\{address\}\}/g, organizationDetails.address || '');
    compiled = compiled.replace(/\{\{city\}\}/g, organizationDetails.city || '');
    compiled = compiled.replace(/\{\{state\}\}/g, organizationDetails.state || '');
    compiled = compiled.replace(/\{\{country\}\}/g, organizationDetails.country || '');
    compiled = compiled.replace(/\{\{pincode\}\}/g, organizationDetails.pincode || '');
    compiled = compiled.replace(/\{\{contactNumber\}\}/g, organizationDetails.contactNumber || '');
    compiled = compiled.replace(/\{\{email\}\}/g, organizationDetails.email || '');
    compiled = compiled.replace(/\{\{website\}\}/g, organizationDetails.website || '');

    // Card dimensions placeholders
    compiled = compiled.replace(/\{\{cardType\}\}/g, cardConfiguration.cardType || 'ID Card');
    compiled = compiled.replace(/\{\{orientation\}\}/g, cardConfiguration.orientation || 'Vertical');
    const dims = cardConfiguration.cardSize === 'Custom Size' ? (cardConfiguration.customCardSize || 'Custom Size') : (cardConfiguration.cardSize || 'Default Size');
    compiled = compiled.replace(/\{\{cardSize\}\}/g, dims);

    // Branding placeholders
    compiled = compiled.replace(/\{\{primaryColor\}\}/g, brandingDetails.primaryColor || '#438CAC');
    compiled = compiled.replace(/\{\{secondaryColor\}\}/g, brandingDetails.secondaryColor || '#f3f2ff');
    compiled = compiled.replace(/\{\{accentColor\}\}/g, brandingDetails.accentColor || '#ffb800');
    compiled = compiled.replace(/\{\{color4\}\}/g, brandingDetails.color4 || '#ffffff');
    compiled = compiled.replace(/\{\{color5\}\}/g, brandingDetails.color5 || '#000000');
    compiled = compiled.replace(/\{\{fontPreference\}\}/g, 'Outfit');
    compiled = compiled.replace(/\{\{logo\}\}/g, brandingDetails.logo ? `Render logo from URL "${brandingDetails.logo}"` : 'Default Logo Box');
    compiled = compiled.replace(/\{\{authoritySignature\}\}/g, brandingDetails.authoritySignature ? `Render signature from URL "${brandingDetails.authoritySignature}"` : 'None');
    compiled = compiled.replace(/\{\{principalSignature\}\}/g, brandingDetails.principalSignature ? `Render signature from URL "${brandingDetails.principalSignature}"` : 'None');
    compiled = compiled.replace(/\{\{stamp\}\}/g, brandingDetails.stamp ? `Render stamp from URL "${brandingDetails.stamp}"` : 'None');

    const authorityDesignationVal = brandingDetails.authorisedSignTag || (cardConfiguration.authorizedSignTag === 'Other' ? (cardConfiguration.customAuthorizedSignTag || 'Authorized Signatory') : (cardConfiguration.authorizedSignTag || 'Principal'));
    compiled = compiled.replace(/\{\{authorityDesignation\}\}/g, authorityDesignationVal);

    // Dynamic Fields resolution
    const frontAll = [...(frontFields || []), ...(frontCustomFields || [])];
    const backAll = [...(backFields || []), ...(backCustomFields || [])];

    const getFieldInstruction = (field) => {
        const lowerField = field.toLowerCase();
        if (lowerField === 'instructions') {
            return `  - Field Item: "Instructions" (Render standard default instructions: 1. This card is non-transferable and must be worn at all times. 2. Loss of this card must be reported immediately to the administration. 3. If found, please return this card to the school office.)`;
        } else if (lowerField === 'terms & conditions' || lowerField === 'terms and conditions') {
            return `  - Field Item: "Terms & Conditions" (Render standard default terms: 1. The cardholder is responsible for the custody of this card. 2. Use of this card is subject to organization policies.)`;
        } else if (lowerField.includes('name')) {
            return `  - Field Item: "${field}" (Design an empty blank bold text slot for the name value - do not render/print any name text value, and do not use any border around this slot)`;
        } else {
            return `  - Field Item: "${field}" (Render the field label "${field}: ", but design the value area completely blank/empty - do not render/print any mock value, and do not display any icon)`;
        }
    };

    let frontFieldsStr = '';
    if (frontAll.length === 0) {
        frontFieldsStr = `- No specific fields assigned to the front side.`;
    } else {
        frontAll.forEach((field) => {
            frontFieldsStr += getFieldInstruction(field) + '\n';
        });
    }

    let backFieldsStr = '';
    if (backAll.length === 0) {
        backFieldsStr = `- No specific fields assigned to the back side.`;
    } else {
        backAll.forEach((field) => {
            backFieldsStr += getFieldInstruction(field) + '\n';
        });
    }

    let lanyardBrandNameVal = '';
    let lanyardLogoVal = '';
    let lanyardPrimaryColorVal = '';
    let lanyardSecondaryColorVal = '';
    let lanyardTextVal = '';
    if (lanyardConfiguration) {
        lanyardBrandNameVal = lanyardConfiguration.brandName || organizationDetails.name || 'N/A';
        lanyardLogoVal = lanyardConfiguration.logo || 'None';
        lanyardPrimaryColorVal = lanyardConfiguration.primaryColor || brandingDetails.primaryColor || '#438CAC';
        lanyardSecondaryColorVal = lanyardConfiguration.secondaryColor || brandingDetails.secondaryColor || '#f3f2ff';

        // Resolve lanyard text value dynamically
        if (lanyardConfiguration.lanyardTextOption === 'Display Custom Text' || lanyardConfiguration.lanyardTextOption === 'Custom Text' || lanyardConfiguration.lanyardText === 'Custom Text') {
            lanyardTextVal = lanyardConfiguration.customText || lanyardConfiguration.lanyardText || '';
        } else if (lanyardConfiguration.lanyardTextOption === 'Display Brand Name / Custom Text') {
            lanyardTextVal = `${lanyardBrandNameVal} / ${lanyardConfiguration.customText || ''}`;
        } else if (lanyardConfiguration.lanyardTextOption === 'Display Brand Name') {
            lanyardTextVal = lanyardBrandNameVal;
        } else {
            // Fallback: if they differ, it was custom text, so return lanyardText or customText
            if (lanyardConfiguration.lanyardText && lanyardBrandNameVal && lanyardConfiguration.lanyardText.trim() !== lanyardBrandNameVal.trim()) {
                lanyardTextVal = lanyardConfiguration.lanyardText || lanyardConfiguration.customText || '';
            } else {
                lanyardTextVal = lanyardBrandNameVal || lanyardConfiguration.lanyardText || '';
            }
        }
    } else {
        lanyardBrandNameVal = 'N/A (Lanyard not enabled)';
        lanyardLogoVal = 'None';
        lanyardPrimaryColorVal = '#438CAC';
        lanyardSecondaryColorVal = '#f3f2ff';
        lanyardTextVal = 'N/A';
    }

    // Resolve lanyard placeholders inside the template instructions (use lanyardTextVal for both to prioritize lanyard text)
    compiled = compiled.replace(/\{\{lanyardBrandName\}\}/g, lanyardTextVal || 'N/A');
    compiled = compiled.replace(/\{\{lanyardLogo\}\}/g, lanyardLogoVal || 'None');
    compiled = compiled.replace(/\{\{lanyardPrimaryColor\}\}/g, lanyardPrimaryColorVal || '#438CAC');
    compiled = compiled.replace(/\{\{lanyardSecondaryColor\}\}/g, lanyardSecondaryColorVal || '#f3f2ff');
    compiled = compiled.replace(/\{\{lanyardText\}\}/g, lanyardTextVal || 'N/A');

    // Now dynamically construct the FIXED USER INPUTS block based on the side:
    let userInputsBlock = '';
    if (side === 'front') {
        const designLanguageVal = cardConfiguration.designLanguage || 'English';
        const cardTypeVal = cardConfiguration.cardType || 'Student ID Card';
        const colourVal = cardConfiguration.designColor === 'Custom' ? (cardConfiguration.customColor || '#438CAC') : (cardConfiguration.designColor || 'Royal Blue');
        const orientationVal = cardConfiguration.orientation || 'Vertical';
        const organisationLabel = organizationDetails.organisationType === 'Custom' ? (organizationDetails.customOrganisationType || 'Custom') : (organizationDetails.organisationType || (organizationDetails.type && organizationDetails.type !== 'School' ? organizationDetails.type : 'Organisation Type'));
        const organisationNameVal = organizationDetails.organisationValue || organizationDetails.additionalInfo || 'N/A';
        const brandNameVal = organizationDetails.name || 'N/A';
        const addressVal = `${organizationDetails.address || ''}${organizationDetails.city ? `, ${organizationDetails.city}` : ''}${organizationDetails.state ? `, ${organizationDetails.state}` : ''}${organizationDetails.country ? `, ${organizationDetails.country}` : ''}${organizationDetails.pincode ? ` - ${organizationDetails.pincode}` : ''}`.trim() || 'N/A';

        let customFieldsList = '';
        if (frontAll && frontAll.length > 0) {
            customFieldsList = frontAll.map(f => `* - ${f}`).join('\n');
        } else {
            customFieldsList = `* - None`;
        }

        userInputsBlock = `
━━━━━━━━━━━━━━━━━━
USER INPUTS
━━━━━━━━━━━━━━━━━━
1. ID Card Design Language - ${designLanguageVal}
2. Card Type - ${cardTypeVal}
3. Colour - ${colourVal}
4. Orientation - ${orientationVal}
5. ${organisationLabel} - ${organisationNameVal}
6. Brand Name - ${brandNameVal}
7. Address - ${addressVal}

* Front Side Custom Fields
${customFieldsList}
* Authorised Sign Tag - ${authorityDesignationVal}

━━━━━━━━━━━━━━━━━━
UPLOADED ASSETS
━━━━━━━━━━━━━━━━━━
Logo: ${brandingDetails.logo ? `Render logo from URL "${brandingDetails.logo}"` : 'Default Logo Box'}
Card Holder Photo: [Design a blank rectangular placeholder slot with a clean white border for the cardholder photo - do not render any face or image inside this slot, keep it completely blank/empty]
Authority Signature: ${brandingDetails.authoritySignature ? `Render signature from URL "${brandingDetails.authoritySignature}"` : 'None'}
School Stamp: ${brandingDetails.stamp ? `Render stamp from URL "${brandingDetails.stamp}"` : 'None'}
Primary Color Theme: ${brandingDetails.primaryColor || '#438CAC'} (Secondary: ${brandingDetails.secondaryColor || '#f3f2ff'}, Accent: ${brandingDetails.accentColor || '#ffb800'}, Color 4: ${brandingDetails.color4 || '#ffffff'}, Color 5: ${brandingDetails.color5 || '#000000'})
Information Fields: ${frontFieldsStr.trim()}
Display ONLY the fields provided by the user.
`;
    } else if (side === 'back') {
        const backFieldsPlainList = backAll.length > 0
            ? backAll.join('\n')
            : 'Date of Birth\nMobile No.\nAadhar No.\nAddress';

        userInputsBlock = `
━━━━━━━━━━━━━━━━━━
USER INPUTS
━━━━━━━━━━━━━━━━━━

Organization Name:
${organizationDetails.name || 'N/A'}

Organization Address:
${organizationDetails.address || ''}${organizationDetails.city ? `, ${organizationDetails.city}` : ''}${organizationDetails.state ? `, ${organizationDetails.state}` : ''}${organizationDetails.country ? `, ${organizationDetails.country}` : ''}${organizationDetails.pincode ? ` - ${organizationDetails.pincode}` : ''}

Additional Information (Optional):
${(designPreferences.notes && designPreferences.notes.trim()) ? designPreferences.notes : 'None'}

Logo:
${brandingDetails.logo ? `Render logo from URL "${brandingDetails.logo}"` : 'Default Logo Box'}

Primary Color Theme:
${brandingDetails.primaryColor || '#438CAC'} (Secondary: ${brandingDetails.secondaryColor || '#f3f2ff'}, Accent: ${brandingDetails.accentColor || '#ffb800'}, Color 4: ${brandingDetails.color4 || '#ffffff'}, Color 5: ${brandingDetails.color5 || '#000000'})

Information Fields:
${backFieldsStr.trim()}

Display ONLY the fields provided by the user.

${backFieldsPlainList}
`;
    } else if (side === 'lanyard') {
        userInputsBlock = `
━━━━━━━━━━━━━━━━━━
USER INPUTS
━━━━━━━━━━━━━━━━━━

Lanyard Text:
${lanyardTextVal}

Lanyard Logo File:
${lanyardLogoVal}

Primary Lanyard Color:
${lanyardPrimaryColorVal}

Secondary Accent Color:
${lanyardSecondaryColorVal}

Visual Font Style:
Outfit

Additional Instructions (Optional):
${(designPreferences.notes && designPreferences.notes.trim()) ? designPreferences.notes : 'None'}
`;
    }

    if (side === 'lanyard') {
        compiled = compiled.replace(/Brand Name/g, 'Lanyard Text');
        compiled = compiled.replace(/brand name/g, 'lanyard text');
    }

    return compiled.trim() + '\n\n' + userInputsBlock.trim();
};

function SchoolSetup() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    // Extract vendor ID if logged in as vendor
    const getVendorId = () => {
        const role = sessionStorage.getItem('ID_CardAdmin_user_role');
        const userData = sessionStorage.getItem('ID_CardAdmin_user');
        console.log('getVendorId called. role:', role, 'userData:', userData);
        if (role === 'vendor' && userData) {
            try {
                const parsed = JSON.parse(userData);
                const vendorIdVal = parsed._id || parsed.id || parsed.vendor_id || parsed.vendorid || parsed.phone_no || null;
                console.log('getVendorId resolved to:', vendorIdVal);
                return vendorIdVal;
            } catch (e) {
                console.error('Error parsing vendor user data:', e);
            }
        }
        return null;
    };

    // Mode States
    const isUserLoggedIn = sessionStorage.getItem("ID_CardAdmin_user") !== null;
    const queryParams = new URLSearchParams(window.location.search);
    const isViewAdmin = queryParams.get('view') === 'admin';
    const [isAdminMode, setIsAdminMode] = useState(isViewAdmin && isUserLoggedIn);
    const [submissions, setSubmissions] = useState([]);
    const [selectedSchoolConfig, setSelectedSchoolConfig] = useState(null);
    const [selectedSchoolIndex, setSelectedSchoolIndex] = useState(-1);
    const [adminSearchQuery, setAdminSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const getLanyardTextOptionDisplay = (config) => {
        if (!config || !config.lanyardConfiguration) return 'None';
        const lc = config.lanyardConfiguration;
        const orgName = config.organizationDetails?.name || '';
        const brandName = lc.brandName || orgName;

        if (lc.lanyardTextOption === 'Display Custom Text' || lc.lanyardTextOption === 'Custom Text') {
            return 'Custom Text';
        }

        if (lc.lanyardText === 'Custom Text') {
            return 'Custom Text';
        }

        if (lc.lanyardTextOption === 'Display Brand Name') {
            return 'Brand Name';
        }

        if (lc.lanyardText && brandName && lc.lanyardText.trim() !== brandName.trim()) {
            return 'Custom Text';
        }

        return 'Brand Name';
    };

    const getLanyardTextDisplay = (config) => {
        if (!config || !config.lanyardConfiguration) return 'N/A';
        const lc = config.lanyardConfiguration;
        const orgName = config.organizationDetails?.name || '';

        if (lc.lanyardTextOption === 'Display Custom Text' || lc.lanyardTextOption === 'Custom Text' || lc.lanyardText === 'Custom Text') {
            return lc.customText || lc.lanyardText || 'N/A';
        }

        if (lc.lanyardTextOption === 'Display Brand Name') {
            return lc.brandName || orgName || 'N/A';
        }

        const brandName = lc.brandName || orgName;
        if (lc.lanyardText && brandName && lc.lanyardText.trim() !== brandName.trim()) {
            return lc.lanyardText || lc.customText || 'N/A';
        }

        return brandName || lc.lanyardText || 'N/A';
    };

    // List Configurations from backend (Supports search and pagination)
    const fetchSubmissions = async () => {
        setIsLoading(true);
        try {
            const token = sessionStorage.getItem('ID_CardAdmin_token') || sessionStorage.getItem('token') || '';
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const backendPage = page + 1;

            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/school-setup/list`, {
                params: {
                    page: backendPage,
                    limit: rowsPerPage,
                    search: adminSearchQuery
                },
                headers
            });

            if (response.data && response.data.success) {
                setSubmissions(response.data.data || []);
                setTotalCount(response.data.totalCount || 0);
            } else {
                enqueueSnackbar(response.data?.message || 'Failed to fetch configurations', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
            // Handle unauthorized errors gracefully
            if (error.response?.status === 401 || error.response?.status === 403) {
                enqueueSnackbar('Authentication required to view configurations.', { variant: 'warning' });
            } else {
                enqueueSnackbar(error.response?.data?.message || 'Error fetching submissions from server', { variant: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenDetails = (sub, idx) => {
        setSelectedSchoolConfig(sub);
        setSelectedSchoolIndex(idx);
        setAdminTabValue(0);
        setDialogOpen(true);
    };

    // Prompt generator settings (for Admin Mode)
    const [adminTabValue, setAdminTabValue] = useState(0); // 0 = Prompt, 1 = JSON Payload
    const [promptSideTab, setPromptSideTab] = useState(0); // 0 = Front, 1 = Back, 2 = Lanyard

    const [frontTemplate, setFrontTemplate] = useState(() => {
        const stored = localStorage.getItem('frontTemplate');
        if (stored && stored.includes('USER INPUTS')) {
            localStorage.setItem('frontTemplate', DEFAULT_FRONT_TEMPLATE);
            return DEFAULT_FRONT_TEMPLATE;
        }
        if (stored && !stored.includes('Tailwind') && !stored.includes('HTML') && !stored.includes('[TARGET AGENT:')) {
            return stored;
        }
        return DEFAULT_FRONT_TEMPLATE;
    });
    const [backTemplate, setBackTemplate] = useState(() => {
        const stored = localStorage.getItem('backTemplate');
        if (stored && stored.includes('USER INPUTS')) {
            localStorage.setItem('backTemplate', DEFAULT_BACK_TEMPLATE);
            return DEFAULT_BACK_TEMPLATE;
        }
        if (stored && !stored.includes('Tailwind') && !stored.includes('HTML') && !stored.includes('[TARGET AGENT:')) {
            return stored;
        }
        return DEFAULT_BACK_TEMPLATE;
    });
    const [lanyardTemplate, setLanyardTemplate] = useState(() => {
        const stored = localStorage.getItem('lanyardTemplate');
        if (stored && stored.includes('USER INPUTS')) {
            localStorage.setItem('lanyardTemplate', DEFAULT_LANYARD_TEMPLATE);
            return DEFAULT_LANYARD_TEMPLATE;
        }
        if (stored && !stored.includes('Tailwind') && !stored.includes('HTML') && !stored.includes('[TARGET AGENT:')) {
            return stored;
        }
        return DEFAULT_LANYARD_TEMPLATE;
    });

    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [resetSide, setResetSide] = useState(null);

    const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
    const [deleteConfigId, setDeleteConfigId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleResetClick = (side) => {
        setResetSide(side);
        setOpenResetDialog(true);
    };

    const handleConfirmReset = () => {
        if (!resetSide) return;

        let targetSetTemplate;
        let defaultTemplate;

        if (resetSide === 'front') {
            targetSetTemplate = setFrontTemplate;
            defaultTemplate = DEFAULT_FRONT_TEMPLATE;
        } else if (resetSide === 'back') {
            targetSetTemplate = setBackTemplate;
            defaultTemplate = DEFAULT_BACK_TEMPLATE;
        } else if (resetSide === 'lanyard') {
            targetSetTemplate = setLanyardTemplate;
            defaultTemplate = DEFAULT_LANYARD_TEMPLATE;
        }

        if (targetSetTemplate) {
            targetSetTemplate(defaultTemplate);
            localStorage.setItem(`${resetSide}Template`, defaultTemplate);
            enqueueSnackbar(`${resetSide.charAt(0).toUpperCase() + resetSide.slice(1)} template reset to default`, { variant: 'info' });
        }

        setOpenResetDialog(false);
        setResetSide(null);
    };

    useEffect(() => {
        localStorage.setItem('frontTemplate', frontTemplate);
    }, [frontTemplate]);

    useEffect(() => {
        localStorage.setItem('backTemplate', backTemplate);
    }, [backTemplate]);

    useEffect(() => {
        localStorage.setItem('lanyardTemplate', lanyardTemplate);
    }, [lanyardTemplate]);

    const handleCopySidePrompt = (text, side) => {
        navigator.clipboard.writeText(text)
            .then(() => enqueueSnackbar(`${side === 'front' ? 'Front' : side === 'back' ? 'Back' : 'Lanyard'} AI prompt copied successfully!`, { variant: 'success' }))
            .catch(err => {
                console.error(err);
                enqueueSnackbar('Copy failed', { variant: 'error' });
            });
    };

    const handleDownloadAsset = async (url, filename) => {
        if (!url) return;
        try {
            enqueueSnackbar(`Starting download...`, { variant: 'info' });
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'asset';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            enqueueSnackbar(`Download completed successfully!`, { variant: 'success' });
        } catch (error) {
            console.error("Download failed, opening in new tab:", error);
            window.open(url, '_blank');
        }
    };

    const insertPlaceholderAtCursor = (side, placeholderValue) => {
        let currentText = '';
        let setText = null;
        let elementId = '';

        if (side === 'front') {
            currentText = frontTemplate;
            setText = setFrontTemplate;
            elementId = 'front-template-textarea';
        } else if (side === 'back') {
            currentText = backTemplate;
            setText = setBackTemplate;
            elementId = 'back-template-textarea';
        } else {
            currentText = lanyardTemplate;
            setText = setLanyardTemplate;
            elementId = 'lanyard-template-textarea';
        }

        const textarea = document.getElementById(elementId);
        if (!textarea) {
            const newText = currentText + ' ' + placeholderValue;
            setText(newText);
            localStorage.setItem(`${side}Template`, newText);
            return;
        }

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const textBefore = currentText.substring(0, start);
        const textAfter = currentText.substring(end, currentText.length);

        const newText = textBefore + placeholderValue + textAfter;
        setText(newText);
        localStorage.setItem(`${side}Template`, newText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + placeholderValue.length, start + placeholderValue.length);
        }, 50);
    };

    const renderPromptEditorAndPreview = (side) => {
        let template = '';
        let setTemplate = null;
        let defaultTemplate = '';
        let textareaId = '';

        if (side === 'front') {
            template = frontTemplate;
            setTemplate = setFrontTemplate;
            defaultTemplate = DEFAULT_FRONT_TEMPLATE;
            textareaId = 'front-template-textarea';
        } else if (side === 'back') {
            template = backTemplate;
            setTemplate = setBackTemplate;
            defaultTemplate = DEFAULT_BACK_TEMPLATE;
            textareaId = 'back-template-textarea';
        } else {
            template = lanyardTemplate;
            setTemplate = setLanyardTemplate;
            defaultTemplate = DEFAULT_LANYARD_TEMPLATE;
            textareaId = 'lanyard-template-textarea';
        }

        const compiled = compileTemplate(template, selectedSchoolConfig, side);

        const assetsToDownload = [];
        if (selectedSchoolConfig) {
            if (side === 'front' || side === 'back') {
                const b = selectedSchoolConfig.brandingDetails || {};
                if (b.logo) {
                    assetsToDownload.push({ label: 'Organization Logo', url: b.logo, filename: `school_logo_${selectedSchoolConfig.organizationDetails?.name || 'logo'}` });
                }
                if (b.authoritySignature) {
                    assetsToDownload.push({ label: 'Authority Signature', url: b.authoritySignature, filename: 'authority_signature' });
                }
                if (b.principalSignature) {
                    assetsToDownload.push({ label: 'Principal Signature', url: b.principalSignature, filename: 'principal_signature' });
                }
                if (b.stamp) {
                    assetsToDownload.push({ label: 'School Stamp', url: b.stamp, filename: 'school_stamp' });
                }
            } else if (side === 'lanyard') {
                const lc = selectedSchoolConfig.lanyardConfiguration || {};
                if (lc.logo) {
                    assetsToDownload.push({ label: 'Lanyard Logo', url: lc.logo, filename: 'lanyard_logo' });
                }
            }
        }

        return (
            <Grid container spacing={3} className="prompt-editor-preview-container">
                {/* Left Panel: Template Editor & Placeholders */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit' }}>
                            Edit Prompt Instructions (Dynamic inputs are appended automatically)
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => handleResetClick(side)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Reset to Default
                        </Button>
                    </Box>

                    <TextField
                        id={textareaId}
                        multiline
                        rows={24}
                        value={template}
                        onChange={(e) => {
                            setTemplate(e.target.value);
                            localStorage.setItem(`${side}Template`, e.target.value);
                        }}
                        fullWidth
                        variant="outlined"
                        placeholder="Write your prompt template here..."
                        InputProps={{
                            style: {
                                fontFamily: 'Consolas, Courier New, monospace',
                                fontSize: '0.85rem',
                                background: '#fcfcfe',
                                lineHeight: '1.5'
                            }
                        }}
                    />

                </Grid>

                {/* Right Panel: Compiled Output Live Preview */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#2e7d32', fontFamily: 'Outfit' }}>
                            Compiled AI Prompt Output (Live)
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            className="primary-btn"
                            onClick={() => handleCopySidePrompt(compiled, side)}
                            startIcon={<ContentCopyIcon />}
                            sx={{ py: 0.5, px: 2, height: '32px' }}
                        >
                            Copy Prompt
                        </Button>
                    </Box>

                    <Box
                        className="prompt-output-container"
                        sx={{
                            height: '480px',
                            overflowY: 'auto',
                            border: '1px solid #cbd5e1',
                            background: '#f8fafc',
                            borderRadius: '12px',
                            p: 2
                        }}
                    >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'Consolas, Courier New, monospace', fontSize: '0.8rem', color: '#1e293b', lineHeight: '1.6' }}>
                            {compiled}
                        </pre>
                    </Box>

                    {assetsToDownload.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, border: '1px dashed #cbd5e1', borderRadius: '12px', background: '#fafbfe' }}>
                            <Typography variant="caption" style={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1 }}>
                                Download Uploaded Assets for AI Prompts:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {assetsToDownload.map((asset) => (
                                    <Button
                                        key={asset.label}
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => handleDownloadAsset(asset.url, asset.filename)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                    >
                                        {asset.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Grid>

                {/* Bottom Panel: Form-Fetched Reference Values */}
                {selectedSchoolConfig && (
                    <Grid item xs={12}>
                        <Box sx={{ mt: 1, p: 3, border: '1px solid rgba(75, 73, 172, 0.15)', borderRadius: '12px', background: '#fafbfe' }}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <ListAltIcon fontSize="small" /> Form-Fetched School Setup Fields (Reference)
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: '1.8' }}>
                                        <strong>Organization Name:</strong> {selectedSchoolConfig.organizationDetails?.name || 'N/A'}
                                        <br />
                                        <strong>Organization Address:</strong> {selectedSchoolConfig.organizationDetails?.address || 'N/A'}
                                        {selectedSchoolConfig.organizationDetails?.city ? `, ${selectedSchoolConfig.organizationDetails.city}` : ''}
                                        {selectedSchoolConfig.organizationDetails?.state ? `, ${selectedSchoolConfig.organizationDetails.state}` : ''}
                                        {selectedSchoolConfig.organizationDetails?.pincode ? ` - ${selectedSchoolConfig.organizationDetails.pincode}` : ''}
                                        <br />
                                        <strong>Logo URL:</strong> {selectedSchoolConfig.brandingDetails?.logo ? <a href={selectedSchoolConfig.brandingDetails.logo} target="_blank" rel="noopener noreferrer">View Logo</a> : 'None'}
                                        <br />
                                        <strong>Authority Signature:</strong> {selectedSchoolConfig.brandingDetails?.authoritySignature ? <a href={selectedSchoolConfig.brandingDetails.authoritySignature} target="_blank" rel="noopener noreferrer">View Signature</a> : 'None'}
                                        <br />
                                        <strong>School Stamp:</strong> {selectedSchoolConfig.brandingDetails?.stamp ? <a href={selectedSchoolConfig.brandingDetails.stamp} target="_blank" rel="noopener noreferrer">View Stamp</a> : 'None'}
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: '1.8' }}>
                                        <strong>Primary Color Theme:</strong> {selectedSchoolConfig.brandingDetails?.primaryColor || 'N/A'} (Secondary: {selectedSchoolConfig.brandingDetails?.secondaryColor || 'N/A'}, Accent: {selectedSchoolConfig.brandingDetails?.accentColor || 'N/A'})
                                        <br />
                                        <strong>Information Fields (Front):</strong> {[...(selectedSchoolConfig.frontFields || []), ...(selectedSchoolConfig.frontCustomFields || [])].join(', ') || 'None'}
                                        <br />
                                        <strong>Information Fields (Back):</strong> {[...(selectedSchoolConfig.backFields || []), ...(selectedSchoolConfig.backCustomFields || [])].join(', ') || 'None'}
                                        <br />
                                        <strong>Lanyard Text Option:</strong> {getLanyardTextOptionDisplay(selectedSchoolConfig)}
                                    </div>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                )}
            </Grid>
        );
    };


    // Form Wizard States (School Mode)
    const [activeStep, setActiveStep] = useState(0);
    const [selectedRegisterSchool, setSelectedRegisterSchool] = useState('');
    const [customSchoolName, setCustomSchoolName] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [orgDetails, setOrgDetails] = useState({
        organisationType: 'Sanstha Name', // 'Sanstha Name' | 'Reg. No.' | 'UDISE No.' | 'Custom'
        customOrganisationType: '',       // The custom organization type name if 'Custom' is selected
        organisationValue: '',            // The registration number or other identifier
        name: '',                         // Brand Name (School / College / Institution Name)
        address: '',                      // Full Address
        contactNumber: '',                // Contact No.
        email: '',                        // Email ID
        website: '',                      // Website
        type: 'School'                    // Kept for backend validation fallback
    });

    const [brandingDetails, setBrandingDetails] = useState({
        logo: null,                 // Organisation Logo
        authoritySignature: null,   // Authorised Signature
        stamp: null,                // Any Other Logo or Image (School Stamp)
        primaryColor: '#438CAC',
        secondaryColor: '#f3f2ff',
        accentColor: '#ffb800'
    });

    const [activeColorsCount, setActiveColorsCount] = useState(2);

    const handleAddColorClick = () => {
        if (activeColorsCount < 5) {
            setActiveColorsCount(prev => prev + 1);
        }
    };

    const handleRemoveColor = (colorNum) => {
        if (colorNum === 3) {
            setBrandingDetails(prev => ({
                ...prev,
                accentColor: prev.color4 || '#ffffff',
                color4: prev.color5 || '#000000',
                color5: '#000000'
            }));
        } else if (colorNum === 4) {
            setBrandingDetails(prev => ({
                ...prev,
                color4: prev.color5 || '#000000',
                color5: '#000000'
            }));
        } else if (colorNum === 5) {
            setBrandingDetails(prev => ({
                ...prev,
                color5: '#000000'
            }));
        }
        setActiveColorsCount(prev => Math.max(2, prev - 1));
    };

    const [cardConfig, setCardConfig] = useState({
        designLanguage: 'English',      // 'English' | 'Marathi'
        cardType: 'Student',            // 'Student' | 'Staff'
        designColor: 'Royal Blue',      // 'Red' | 'Blue' | 'Royal Blue' | 'Brown' | 'Yellow' | 'Orange' | 'Green' | 'Custom'
        customColor: '#438CAC',         // Custom Color picker hex value
        orientation: 'Vertical',        // 'Vertical' | 'Horizontal'
        layoutType: 'Both Side',        // 'Both Side' | 'One Side'
        generateLanyard: 'Yes',         // 'Yes' | 'No'
        authorizedSignTag: 'Principal', // Dropdown options
        customAuthorizedSignTag: ''     // Custom Authorized Signature Tag text
    });

    const [frontFields, setFrontFields] = useState([]);
    const [frontCustomFieldInput, setFrontCustomFieldInput] = useState('');
    const [frontCustomFields, setFrontCustomFields] = useState([]);
    const [selectedFrontFieldsOptions, setSelectedFrontFieldsOptions] = useState([]);

    const [backFields, setBackFields] = useState([]);
    const [backCustomFieldInput, setBackCustomFieldInput] = useState('');
    const [backCustomFields, setBackCustomFields] = useState([]);
    const [selectedBackFieldsOptions, setSelectedBackFieldsOptions] = useState([]);

    // Card Placeholder States
    const [selectedCardPlaceholder, setSelectedCardPlaceholder] = useState('1.White.jpeg');

    // Class, Division & House States
    const [classesList, setClassesList] = useState([]);
    const [classFormatStyle, setClassFormatStyle] = useState('Regular'); // 'Regular' | 'Roman'
    const [selectedClassesOptions, setSelectedClassesOptions] = useState([]);
    const [customClassValue, setCustomClassValue] = useState('');

    const [divisionsList, setDivisionsList] = useState([]);
    const [selectedDivisionsOptions, setSelectedDivisionsOptions] = useState([]);
    const [customDivisionValue, setCustomDivisionValue] = useState('');

    const [housesList, setHousesList] = useState([]);
    const [selectedHousesOptions, setSelectedHousesOptions] = useState([]);
    const [customHouseValue, setCustomHouseValue] = useState('');

    // Dynamic custom fields count and values
    const [frontCustomFieldsCount, setFrontCustomFieldsCount] = useState(0);
    const [frontCustomFieldLabels, setFrontCustomFieldLabels] = useState([]);

    const [backCustomFieldsCount, setBackCustomFieldsCount] = useState(0);
    const [backCustomFieldLabels, setBackCustomFieldLabels] = useState([]);

    const [lanyardConfig, setLanyardConfig] = useState({
        lanyardTextOption: 'Display Brand Name', // 1. Brand Name, 2. Custom Text, 3. Brand Name / Custom Text
        customText: '',                          // Custom text value
        brandName: '',
        logo: null,
        primaryColor: '#438CAC',
        secondaryColor: '#f3f2ff'
    });

    const [designPreferences, setDesignPreferences] = useState({
        theme: 'Premium',
        colorStyle: 'Gradient',
        notes: ''
    });

    const activeStepsList = [
        'ID Card Config',
        'Organisation Details',
        'ID Card Placeholder',
        'Front Side Fields',
        ...(cardConfig.layoutType === 'Both Side' ? ['Back Side Fields'] : []),
        'Upload Assets',
        ...(cardConfig.generateLanyard === 'Yes' ? ['Lanyard Config'] : []),
        'Class & House Setup'
    ];

    // Dynamic fetch configurations from backend in Admin Mode
    useEffect(() => {
        if (isAdminMode) {
            fetchSubmissions();
        }
    }, [isAdminMode, page, rowsPerPage, adminSearchQuery]);

    // Automatically synchronize brandingDetails and lanyardConfig colors with designColor / customColor from ID Card Config
    useEffect(() => {
        let primary = '#438CAC';
        let secondary = '#f3f2ff';

        if (cardConfig.designColor === 'Custom') {
            primary = cardConfig.customColor || '#438CAC';
            if (primary.startsWith('#') && primary.length === 7) {
                secondary = primary + '1A'; // 10% opacity hex
            }
        } else if (DESIGN_COLOR_MAP[cardConfig.designColor]) {
            primary = DESIGN_COLOR_MAP[cardConfig.designColor].primary;
            secondary = DESIGN_COLOR_MAP[cardConfig.designColor].secondary;
        }

        setBrandingDetails(prev => ({
            ...prev,
            primaryColor: primary,
            secondaryColor: secondary
        }));

        setLanyardConfig(prev => ({
            ...prev,
            primaryColor: primary,
            secondaryColor: secondary
        }));
    }, [cardConfig.designColor, cardConfig.customColor]);

    // Load config details into admin review pane
    const loadAdminSchoolConfig = (config, index) => {
        setSelectedSchoolConfig(config);
        setSelectedSchoolIndex(index);
    };

    // School selection handler in Form Wizard
    const handleSelectRegisterSchool = (e) => {
        const val = e.target.value;
        setSelectedRegisterSchool(val);
        if (val !== 'Other') {
            setOrgDetails(prev => ({ ...prev, name: val }));
            setCustomSchoolName('');
        } else {
            setOrgDetails(prev => ({ ...prev, name: '' }));
        }
    };

    // Change handlers
    const handleOrgChange = (e) => {
        const { name, value } = e.target;
        setOrgDetails(prev => ({ ...prev, [name]: value }));
    };

    const handleBrandingChange = (name, value) => {
        setBrandingDetails(prev => ({ ...prev, [name]: value }));
    };

    const handleCardChange = (e) => {
        const { name, value } = e.target;
        setCardConfig(prev => {
            const nextConfig = { ...prev, [name]: value };
            if (name === 'designLanguage') {
                nextConfig.authorizedSignTag = value === 'Marathi' ? 'मुख्याध्यापक' : 'Principal';
                nextConfig.customAuthorizedSignTag = '';
            }
            return nextConfig;
        });
    };

    const handleLanyardChange = (name, value) => {
        setLanyardConfig(prev => ({ ...prev, [name]: value }));
    };

    const handleDesignChange = (e) => {
        const { name, value } = e.target;
        setDesignPreferences(prev => ({ ...prev, [name]: value }));
    };

    const handleFrontFieldToggle = (field) => {
        setFrontFields(prev =>
            prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
        );
    };

    const handleBackFieldToggle = (field) => {
        setBackFields(prev =>
            prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
        );
    };

    // Custom fields add/delete
    const addFrontCustomField = () => {
        if (frontCustomFieldInput.trim() && !frontCustomFields.includes(frontCustomFieldInput.trim())) {
            setFrontCustomFields(prev => [...prev, frontCustomFieldInput.trim()]);
            setFrontCustomFieldInput('');
            enqueueSnackbar('Custom front field added', { variant: 'info' });
        }
    };

    const addBackCustomField = () => {
        if (backCustomFieldInput.trim() && !backCustomFields.includes(backCustomFieldInput.trim())) {
            setBackCustomFields(prev => [...prev, backCustomFieldInput.trim()]);
            setBackCustomFieldInput('');
            enqueueSnackbar('Custom back field added', { variant: 'info' });
        }
    };

    // Helper to get filename from S3 public URL
    const getFileNameFromUrl = (url) => {
        if (!url) return '';
        try {
            if (url.startsWith('http://') || url.startsWith('https://')) {
                const parsed = new URL(url);
                const parts = parsed.pathname.split('/');
                const filenameWithTimestamp = parts[parts.length - 1];
                return decodeURIComponent(filenameWithTimestamp);
            }
        } catch (e) {
            console.error("Error parsing filename from URL:", e);
        }
        return url;
    };

    // Real file upload to S3 using pre-signed upload URL
    const handleFileUpload = async (field, e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const fileName = file.name;
            const fileType = file.type || 'image/png';

            enqueueSnackbar(`Uploading ${fileName}...`, { variant: 'info' });

            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/school-setup/get-upload-url`, {
                    params: {
                        fileType: fileType,
                        fileName: fileName
                    }
                });

                if (response.data && response.data.success) {
                    const { uploadUrl, publicUrl } = response.data;

                    await axios.put(uploadUrl, file, {
                        headers: {
                            'Content-Type': fileType
                        }
                    });

                    if (field.startsWith('lanyard')) {
                        setLanyardConfig(prev => ({ ...prev, logo: publicUrl }));
                    } else {
                        setBrandingDetails(prev => ({ ...prev, [field]: publicUrl }));
                    }
                    enqueueSnackbar(`Uploaded "${fileName}" successfully!`, { variant: 'success' });
                } else {
                    enqueueSnackbar('Failed to get upload URL', { variant: 'error' });
                }
            } catch (error) {
                console.error("Upload error:", error);
                enqueueSnackbar('Error uploading file. Please try again.', { variant: 'error' });
            }
        }
    };

    // Stepper navigation
    const handleNext = () => {
        const currentStepName = activeStepsList[activeStep];
        // Validation on Organisation Details step
        if (currentStepName === 'Organisation Details') {
            if (!orgDetails.name.trim() || !orgDetails.address.trim()) {
                enqueueSnackbar('Please fill in all required (*) fields!', { variant: 'warning' });
                return;
            }
        }
        // Validation on Front Side Fields step
        if (currentStepName === 'Front Side Fields') {
            if (selectedFrontFieldsOptions.length > 0) {
                enqueueSnackbar('Please click the "Add Selected Field(s)" button to add the selected predefined fields before proceeding!', { variant: 'warning' });
                return;
            }
            if (frontCustomFieldInput.trim() !== '') {
                enqueueSnackbar('Please click the "Add Custom Field" button to add your custom field before proceeding!', { variant: 'warning' });
                return;
            }
            if (cardConfig.authorizedSignTag === 'Other' && !cardConfig.customAuthorizedSignTag.trim()) {
                enqueueSnackbar('Please specify the Authorised Sign Tag!', { variant: 'warning' });
                return;
            }
        }
        // Validation on Back Side Fields step
        if (currentStepName === 'Back Side Fields') {
            if (selectedBackFieldsOptions.length > 0) {
                enqueueSnackbar('Please click the "Add Selected Field(s)" button to add the selected predefined fields before proceeding!', { variant: 'warning' });
                return;
            }
            if (backCustomFieldInput.trim() !== '') {
                enqueueSnackbar('Please click the "Add Custom Field" button to add your custom field before proceeding!', { variant: 'warning' });
                return;
            }
        }


        if (activeStep < activeStepsList.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        } else {
            // Log out and go back to login page
            sessionStorage.removeItem('ID_CardAdmin_user');
            sessionStorage.removeItem('ID_CardAdmin_user_auth');
            sessionStorage.removeItem('ID_CardAdmin_user_role');
            navigate('/vendor-login', { replace: true });
        }
    };

    // Compile dynamic payload
    const compiledPayload = {
        vendor_id: getVendorId(),
        organizationDetails: {
            ...orgDetails,
            organisationType: orgDetails.organisationType === 'Custom' ? (orgDetails.customOrganisationType || 'Custom') : (orgDetails.organisationType || 'School'),
            organisationValue: orgDetails.organisationValue || '',
            contactNumber: orgDetails.contactNumber && orgDetails.contactNumber.trim() ? orgDetails.contactNumber : 'N/A'
        },
        brandingDetails: {
            ...brandingDetails,
            authorisedSignTag: cardConfig.authorizedSignTag === 'Other' ? (cardConfig.customAuthorizedSignTag || 'Authorized Signatory') : (cardConfig.authorizedSignTag || 'Principal')
        },
        cardConfiguration: {
            ...cardConfig,
            cardType: cardConfig.cardType === 'Student' ? 'Student ID Card' : 'Staff ID Card',
            cardSize: '88 mm × 56 mm (Default)',
            layoutType: cardConfig.layoutType === 'Both Side' ? 'Double Sided' : 'Single Sided'
        },
        frontFields: frontFields,
        frontCustomFields: frontCustomFields,
        backFields: cardConfig.layoutType === 'Both Side' ? backFields : [],
        backCustomFields: cardConfig.layoutType === 'Both Side' ? backCustomFields : [],
        lanyardConfiguration: cardConfig.generateLanyard === 'Yes' ? {
            ...lanyardConfig,
            lanyardText: lanyardConfig.lanyardTextOption === 'Display Custom Text' || lanyardConfig.lanyardTextOption === 'Custom Text'
                ? lanyardConfig.customText
                : lanyardConfig.lanyardTextOption === 'Display Brand Name / Custom Text'
                    ? `${lanyardConfig.brandName || orgDetails.name || ''} / ${lanyardConfig.customText || ''}`
                    : (lanyardConfig.brandName || orgDetails.name || '')
        } : null,
        designPreferences: designPreferences,
        idCardDesignPlaceholder: selectedCardPlaceholder,
        classes: classesList,
        divisions: divisionsList,
        houses: housesList
    };

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(JSON.stringify(compiledPayload, null, 2))
            .then(() => {
                enqueueSnackbar('Submission payload copied to clipboard!', { variant: 'success' });
            })
            .catch(err => {
                console.error(err);
                enqueueSnackbar('Failed to copy payload', { variant: 'error' });
            });
    };

    // Submit handler (School Mode)
    const handleSubmitForm = async () => {
        if (!orgDetails.name.trim()) {
            enqueueSnackbar('Brand Name (School / College / Institution Name) is required!', { variant: 'error' });
            return;
        }
        if (cardConfig.authorizedSignTag === 'Other' && !cardConfig.customAuthorizedSignTag.trim()) {
            enqueueSnackbar('Please specify the Authorised Sign Tag!', { variant: 'error' });
            return;
        }
        if (selectedFrontFieldsOptions.length > 0) {
            enqueueSnackbar('Please click the "Add Selected Field(s)" button on the Front Side Fields step before submitting!', { variant: 'error' });
            return;
        }
        if (frontCustomFieldInput.trim() !== '') {
            enqueueSnackbar('Please click the "Add Custom Field" button on the Front Side Fields step before submitting!', { variant: 'error' });
            return;
        }
        if (cardConfig.layoutType === 'Both Side' && selectedBackFieldsOptions.length > 0) {
            enqueueSnackbar('Please click the "Add Selected Field(s)" button on the Back Side Fields step before submitting!', { variant: 'error' });
            return;
        }
        if (cardConfig.layoutType === 'Both Side' && backCustomFieldInput.trim() !== '') {
            enqueueSnackbar('Please click the "Add Custom Field" button on the Back Side Fields step before submitting!', { variant: 'error' });
            return;
        }
        if (selectedClassesOptions.length > 0) {
            enqueueSnackbar('Please click the "Add Class(es)" button to add selected classes before submitting!', { variant: 'error' });
            return;
        }
        if (selectedClassesOptions.includes('Custom') && customClassValue.trim() !== '') {
            enqueueSnackbar('Please click the "Add Class(es)" button to add your custom class before submitting!', { variant: 'error' });
            return;
        }
        if (selectedDivisionsOptions.length > 0) {
            enqueueSnackbar('Please click the "Add Division(s)" button to add selected divisions before submitting!', { variant: 'error' });
            return;
        }
        if (selectedDivisionsOptions.includes('Custom') && customDivisionValue.trim() !== '') {
            enqueueSnackbar('Please click the "Add Division(s)" button to add your custom division before submitting!', { variant: 'error' });
            return;
        }
        if (selectedHousesOptions.length > 0) {
            enqueueSnackbar('Please click the "Add House(s)" button to add selected houses before submitting!', { variant: 'error' });
            return;
        }
        if (selectedHousesOptions.includes('Custom') && customHouseValue.trim() !== '') {
            enqueueSnackbar('Please click the "Add House(s)" button to add your custom house before submitting!', { variant: 'error' });
            return;
        }

        const payload = compiledPayload;

        try {
            enqueueSnackbar('Submitting configuration setup...', { variant: 'info' });
            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/school-setup/submit`, payload);

            if (response.data && response.data.success) {
                enqueueSnackbar('Setup details successfully submitted to Admin!', { variant: 'success' });
                setIsSubmitted(true);
                const savedData = response.data.data;
                setSelectedSchoolConfig(savedData);
                setSelectedSchoolIndex(0);

                if (isAdminMode) {
                    fetchSubmissions();
                }

                // Log out directly/immediately by clearing session storage
                sessionStorage.removeItem('ID_CardAdmin_user');
                sessionStorage.removeItem('ID_CardAdmin_user_auth');
                sessionStorage.removeItem('ID_CardAdmin_user_role');
            } else {
                enqueueSnackbar(response.data?.message || 'Submission failed', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            enqueueSnackbar(error.response?.data?.message || 'Error submitting form to server', { variant: 'error' });
        }
    };

    // Form Reset
    const handleResetForm = () => {
        setOrgDetails({
            organisationType: 'Sanstha Name',
            customOrganisationType: '',
            organisationValue: '',
            name: '',
            address: '',
            contactNumber: '',
            email: '',
            website: '',
            type: 'School'
        });
        setBrandingDetails({
            logo: null,
            authoritySignature: null,
            stamp: null,
            primaryColor: '#438CAC',
            secondaryColor: '#f3f2ff',
            accentColor: '#ffb800'
        });
        setCardConfig({
            designLanguage: 'English',
            cardType: 'Student',
            designColor: 'Royal Blue',
            customColor: '#438CAC',
            orientation: 'Vertical',
            layoutType: 'Both Side',
            generateLanyard: 'Yes',
            authorizedSignTag: 'Principal',
            customAuthorizedSignTag: ''
        });
        setFrontFields([]);
        setFrontCustomFields([]);
        setSelectedFrontFieldsOptions([]);
        setBackFields([]);
        setBackCustomFields([]);
        setSelectedBackFieldsOptions([]);
        setFrontCustomFieldsCount(0);
        setFrontCustomFieldLabels([]);
        setBackCustomFieldsCount(0);
        setBackCustomFieldLabels([]);
        setLanyardConfig({ lanyardTextOption: 'Display Brand Name', customText: '', brandName: '', logo: null, primaryColor: '#438CAC', secondaryColor: '#f3f2ff' });
        setDesignPreferences({ theme: 'Premium', colorStyle: 'Gradient', notes: '' });
        setSelectedRegisterSchool('');
        setCustomSchoolName('');
        setActiveStep(0);
        setIsSubmitted(false);
        setSelectedCardPlaceholder('1.White.jpeg');
        setClassesList([]);
        setClassFormatStyle('Regular');
        setSelectedClassesOptions([]);
        setCustomClassValue('');
        setDivisionsList([]);
        setSelectedDivisionsOptions([]);
        setCustomDivisionValue('');
        setHousesList([]);
        setSelectedHousesOptions([]);
        setCustomHouseValue('');
    };

    const compilePrompt = (config) => {
        return '';
    };

    const dummyUnusedConsts = () => {
        /*
        prompt += `Act as a creative graphic designer and frontend engineer. Create a pixel-perfect, double-sided ID card layout (front and back wrappers side-by-side or stacked) using semantic HTML and Tailwind CSS (or inline SVGs depending on layout requirements).\n\n`;

        prompt += `[1. ORGANIZATION CONTEXT]\n`;
        prompt += `- Name: ${organizationDetails.name || 'N/A'}\n`;
        prompt += `- Type: ${organizationDetails.type || 'N/A'}\n`;
        prompt += `- Location Details: ${organizationDetails.address || ''}, ${organizationDetails.city || ''}, ${organizationDetails.state || ''}, ${organizationDetails.country || ''} - Pincode: ${organizationDetails.pincode || ''}\n`;
        if (organizationDetails.contactNumber) prompt += `- Contact No: ${organizationDetails.contactNumber}\n`;
        if (organizationDetails.email) prompt += `- Email: ${organizationDetails.email}\n`;
        if (organizationDetails.website) prompt += `- Web: ${organizationDetails.website}\n\n`;

        prompt += `[2. CARD DIMENSIONS & ORIENTATION]\n`;
        prompt += `- Card Category: ${cardConfiguration.cardType || 'ID Card'}\n`;
        prompt += `- Orientation: ${cardConfiguration.orientation || 'Vertical'}\n`;
        prompt += `- Print Dimensions: ${cardConfiguration.cardSize === 'Custom Size' ? (cardConfiguration.customCardSize || 'Custom Size') : cardConfiguration.cardSize}\n\n`;

        prompt += `[3. VISUAL THEME & BRANDING]\n`;
        prompt += `- Core Theme/Style: ${designPreferences.theme || 'Modern'} Vibe\n`;
        prompt += `- Background Style Preferred: ${designPreferences.colorStyle || 'Gradient'}\n`;
        prompt += `- Colors Palette:\n`;
        prompt += `  * Primary Color Theme: ${brandingDetails.primaryColor || '#438CAC'}\n`;
        prompt += `  * Secondary Color Theme: ${brandingDetails.secondaryColor || '#f3f2ff'}\n`;
        prompt += `  * Accent Color Highlight: ${brandingDetails.accentColor || '#ffb800'}\n`;
        prompt += `- Font Style: Outfit\n`;
        prompt += `- Uploaded Branding Assets to Render:\n`;
        prompt += `  * Organization Logo: ${brandingDetails.logo ? `Render placeholder labeled "${brandingDetails.logo}" in header` : 'Default Logo Box'}\n`;
        if (brandingDetails.authoritySignature) prompt += `  * Authority Signature image: ${brandingDetails.authoritySignature} (Render sign frame)\n`;
        if (brandingDetails.principalSignature) prompt += `  * Principal Signature image: ${brandingDetails.principalSignature} (Render sign frame)\n`;
        if (brandingDetails.stamp) prompt += `  * School Stamp: ${brandingDetails.stamp} (Render stamp overlay)\n`;
        prompt += `\n`;

        prompt += `[4. FRONT SIDE FIELD PLACEMENTS]\n`;
        const frontAll = [...(frontFields || []), ...(frontCustomFields || [])];
        if (frontAll.length === 0) {
            prompt += `- No specific fields assigned to the front side.\n`;
        } else {
            frontAll.forEach((field) => {
                prompt += `  - Field Item: "${field}" (Generate placeholder slot)\n`;
            });
        }
        prompt += `\n`;

        prompt += `[5. BACK SIDE FIELD PLACEMENTS]\n`;
        const backAll = [...(backFields || []), ...(backCustomFields || [])];
        if (backAll.length === 0) {
            prompt += `- No specific fields assigned to the back side.\n`;
        } else {
            backAll.forEach((field) => {
                prompt += `  - Field Item: "${field}" (Generate placeholder slot)\n`;
            });
        }
        prompt += `\n`;

        if (lanyardConfiguration) {
            prompt += `[6. LANYARD DESIGN SPECIFICATIONS]\n`;
            prompt += `- Brand Name: ${lanyardConfiguration.brandName || organizationDetails.name}\n`;
            prompt += `- Logo File: ${lanyardConfiguration.logo || 'None'}\n`;
            prompt += `- Primary Lanyard Color: ${lanyardConfiguration.primaryColor}\n`;
            prompt += `- Secondary Accent Color: ${lanyardConfiguration.secondaryColor}\n`;
            prompt += `- Lanyard Content Text: ${lanyardConfiguration.lanyardText === 'Custom Text' ? lanyardConfiguration.customText : lanyardConfiguration.lanyardText}\n\n`;
        }

        if (designPreferences.notes && designPreferences.notes.trim()) {
            prompt += `[7. SPECIAL CUSTOM DESIGN INSTRUCTIONS]\n`;
            prompt += `${designPreferences.notes}\n\n`;
        }

        prompt += `[OUTPUT PROTOCOL]\n`;
        if (targetAgent.includes('HTML')) {
            prompt += `- Output a single, clean, production-ready file of semantic HTML code featuring modern utility styling (Tailwind CSS preferred).\n`;
            prompt += `- Design two responsive cards side-by-side (with IDs "front-card" and "back-card") representing the front and back of the ID card.\n`;
            prompt += `- Structure card containers with premium design aesthetics: rounded corners, realistic soft drop-shadows, border highlights, and clear padding.\n`;
            prompt += `- Populate all selected fields with realistic sample mock data (e.g. Student Name: "Alex Mercer", ID No: "IDS-7890", photo slot container) rather than leaving them blank, so the template renders a ready-to-preview design.\n`;
            prompt += `- Adhere strictly to the requested card size and scale coordinates correctly.`;
        } else if (targetAgent.includes('SVG')) {
            prompt += `- Output two separate vector SVG code snippets (one for the front face and one for the back face).\n`;
            prompt += `- Apply consistent branding colors, rounded corner styles, and premium fonts across both graphics.\n`;
            prompt += `- Populate all fields with realistic mock data values for a complete visual demonstration.`;
        } else {
            prompt += `- Write a highly descriptive, detailed prompt for Midjourney/DALL-E to generate a realistic dual-view mockup of the ID card.\n`;
            prompt += `- Specify soft studio lighting, isolated product placement, high contrast, clean minimalist background, and sharp graphic design details.`;
        }

        return prompt;
    };

    const handleCopyPrompt = (config) => {
        const prompt = compilePrompt(config);
        navigator.clipboard.writeText(prompt)
            .then(() => enqueueSnackbar('AI Prompt copied successfully!', { variant: 'success' }))
            .catch(err => {
                console.error(err);
                enqueueSnackbar('Copy failed', { variant: 'error' });
            });
    };
    */
    };

    const handleDeleteSubmission = (id, e) => {
        if (e) e.stopPropagation();
        setDeleteConfigId(id);
        setOpenDeleteConfirmDialog(true);
    };

    const confirmDeleteSubmission = async () => {
        if (!deleteConfigId) return;
        setIsDeleting(true);
        try {
            const token = sessionStorage.getItem('ID_CardAdmin_token') || sessionStorage.getItem('token') || '';
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/school-setup/delete/${deleteConfigId}`, {
                headers
            });

            if (response.data && response.data.success) {
                enqueueSnackbar('Submission record deleted successfully!', { variant: 'success' });
                fetchSubmissions();

                if (selectedSchoolConfig && selectedSchoolConfig.id === deleteConfigId) {
                    setSelectedSchoolConfig(null);
                    setSelectedSchoolIndex(-1);
                    setDialogOpen(false);
                }
            } else {
                enqueueSnackbar(response.data?.message || 'Failed to delete record', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error deleting submission:', error);
            enqueueSnackbar(error.response?.data?.message || 'Error deleting submission from server', { variant: 'error' });
        } finally {
            setIsDeleting(false);
            setOpenDeleteConfirmDialog(false);
            setDeleteConfigId(null);
        }
    };

    return (
        <div className={`school-setup-page-wrapper ${isAdminMode ? 'admin-layout' : 'school-layout'}`}>
            <StandardPageWrapper
                header="School Configuration Portal"
                btns={null}
            >
                <div className="school-setup-container">

                    {/* 1. PUBLIC SCHOOL SUBMISSION MODE */}
                    {!isAdminMode && (
                        <div className="school-form-mode animate-fade">
                            {isSubmitted ? (
                                <Card className="setup-card thank-you-card elevation-2">
                                    <CheckCircleIcon style={{ color: '#28a745', fontSize: '4.5rem' }} />
                                    <Typography variant="h4" className="my-3 font-weight-bold">Configuration Submitted!</Typography>
                                    <Typography variant="body1" color="textSecondary" align="center" style={{ maxWidth: '500px', marginBottom: '24px' }}>
                                        Thank you! Your school details, card configuration, branding colors, and lanyard options have been submitted.
                                        The administration will now review these parameters and automatically compile the prompt to build your card template.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate('/vendor-login', { replace: true })}
                                        className="primary-btn"
                                        sx={{ px: 4, py: 1.2, fontWeight: 'bold', textTransform: 'none', borderRadius: '8px' }}
                                    >
                                        Go to Login
                                    </Button>
                                </Card>
                            ) : (
                                <div className="school-setup-single-panel">

                                    {/* Step Panel Form */}
                                    <div className="setup-wizard-panel">
                                        <Card className="setup-card elevation-2">
                                            <div className="form-header-row">
                                                <div>
                                                    <Typography variant="h5" style={{ fontWeight: 600, color: '#333' }}>ID Card Setup Form</Typography>
                                                    <Typography variant="caption" color="textSecondary">Configure your organization and card layout preferences</Typography>
                                                </div>
                                                <Button size="small" variant="text" color="error" onClick={handleResetForm}>Reset Form</Button>
                                            </div>

                                            <Box sx={{ width: '100%', mb: 2 }}>
                                                <Stepper activeStep={activeStep} alternativeLabel>
                                                    {activeStepsList.map((label) => (
                                                        <Step key={label}>
                                                            <StepLabel>{label}</StepLabel>
                                                        </Step>
                                                    ))}
                                                </Stepper>
                                            </Box>

                                            {/* Current Step Name Badge */}
                                            <div className="current-step-badge">
                                                <span className="current-step-number">Step {activeStep + 1} of {activeStepsList.length}</span>
                                                <span className="current-step-name">{activeStepsList[activeStep]}</span>
                                            </div>

                                            {/* Dynamic content rendering based on activeStep */}
                                            <div className="setup-step-content-area">

                                                {/* STEP 1: ID Card Config */}
                                                {activeStepsList[activeStep] === 'ID Card Config' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="ID Card Design Language *" name="designLanguage" value={cardConfig.designLanguage || 'English'} onChange={handleCardChange} fullWidth variant="outlined">
                                                                    <MenuItem value="English">English</MenuItem>
                                                                    <MenuItem value="Marathi">Marathi</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="Card Type *" name="cardType" value={cardConfig.cardType || 'Student'} onChange={handleCardChange} fullWidth variant="outlined">
                                                                    <MenuItem value="Student">Student</MenuItem>
                                                                    <MenuItem value="Staff">Staff</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="ID Card Design Colour *" name="designColor" value={cardConfig.designColor || 'Royal Blue'} onChange={handleCardChange} fullWidth variant="outlined">
                                                                    <MenuItem value="Red">Red</MenuItem>
                                                                    <MenuItem value="Blue">Blue</MenuItem>
                                                                    <MenuItem value="Royal Blue">Royal Blue</MenuItem>
                                                                    <MenuItem value="Brown">Brown</MenuItem>
                                                                    <MenuItem value="Yellow">Yellow</MenuItem>
                                                                    <MenuItem value="Orange">Orange</MenuItem>
                                                                    <MenuItem value="Green">Green</MenuItem>
                                                                    <MenuItem value="Custom">Custom</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            {cardConfig.designColor === 'Custom' && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <div className="brand-color-box" style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '12px', height: '56px' }}>
                                                                        <input type="color" value={cardConfig.customColor || '#438CAC'} onChange={(e) => handleCardChange({ target: { name: 'customColor', value: e.target.value } })} style={{ width: '40px', height: '40px', border: 'none', borderRadius: '4px', cursor: 'pointer' }} />
                                                                        <TextField label="Custom Hex Color" value={cardConfig.customColor || '#438CAC'} onChange={(e) => handleCardChange({ target: { name: 'customColor', value: e.target.value } })} variant="standard" size="small" fullWidth />
                                                                    </div>
                                                                </Grid>
                                                            )}
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="Orientation *" name="orientation" value={cardConfig.orientation || 'Vertical'} onChange={handleCardChange} fullWidth variant="outlined">
                                                                    <MenuItem value="Vertical">Vertical (Portrait)</MenuItem>
                                                                    <MenuItem value="Horizontal">Horizontal (Landscape)</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="Card Layout *" name="layoutType" value={cardConfig.layoutType || 'Both Side'} onChange={handleCardChange} fullWidth variant="outlined">
                                                                    <MenuItem value="Both Side">Double Side (Front & Back)</MenuItem>
                                                                    <MenuItem value="One Side">Single Side (Front Only)</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="Generate Lanyard *" name="generateLanyard" value={cardConfig.generateLanyard || 'Yes'} onChange={handleCardChange} fullWidth variant="outlined">
                                                                    <MenuItem value="Yes">Yes</MenuItem>
                                                                    <MenuItem value="No">No</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                        </Grid>
                                                    </div>
                                                )}

                                                {/* STEP 2: Organisation Details */}
                                                {activeStepsList[activeStep] === 'Organisation Details' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField select label="Organisation Type" name="organisationType" value={orgDetails.organisationType || 'Sanstha Name'} onChange={handleOrgChange} fullWidth variant="outlined">
                                                                    <MenuItem value="Sanstha Name">Sanstha Name</MenuItem>
                                                                    <MenuItem value="Reg. No.">Reg. No.</MenuItem>
                                                                    <MenuItem value="UDISE No.">UDISE No.</MenuItem>
                                                                    <MenuItem value="Custom">Custom</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            {orgDetails.organisationType === 'Custom' && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <TextField
                                                                        label="Specify Organisation Type"
                                                                        name="customOrganisationType"
                                                                        value={orgDetails.customOrganisationType || ''}
                                                                        onChange={handleOrgChange}
                                                                        fullWidth
                                                                        variant="outlined"
                                                                    />
                                                                </Grid>
                                                            )}
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField
                                                                    label={
                                                                        orgDetails.organisationType === 'Sanstha Name' ? "Sanstha Name" :
                                                                            orgDetails.organisationType === 'Reg. No.' ? "Registration Number" :
                                                                                orgDetails.organisationType === 'UDISE No.' ? "UDISE Number" :
                                                                                    orgDetails.customOrganisationType ? orgDetails.customOrganisationType : "Custom Organisation Details"
                                                                    }
                                                                    name="organisationValue"
                                                                    value={orgDetails.organisationValue || ''}
                                                                    onChange={handleOrgChange}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <TextField label="Brand Name (School / College / Institution Name) *" name="name" value={orgDetails.name || ''} onChange={handleOrgChange} fullWidth variant="outlined" />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <TextField label="Full Address *" name="address" value={orgDetails.address || ''} onChange={handleOrgChange} fullWidth multiline rows={3} variant="outlined" />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField label="Contact No." name="contactNumber" value={orgDetails.contactNumber || ''} onChange={handleOrgChange} fullWidth variant="outlined" />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField label="Email ID" name="email" value={orgDetails.email || ''} onChange={handleOrgChange} fullWidth variant="outlined" />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <TextField label="Website" name="website" value={orgDetails.website || ''} onChange={handleOrgChange} fullWidth variant="outlined" />
                                                            </Grid>
                                                        </Grid>
                                                    </div>
                                                )}

                                                {/* STEP 2.5: ID Card Placeholder */}
                                                {activeStepsList[activeStep] === 'ID Card Placeholder' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit' }}>
                                                            Select ID Card Design layout template:
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            {[
                                                                '1.White.jpeg',
                                                                '2.Orange.jpeg',
                                                                '3.Yellow.jpeg',
                                                                '4.Red.jpeg',
                                                                '5.Brown.jpeg',
                                                                '6.Blue.jpeg',
                                                                '7.Green.jpeg',
                                                                '8.Crystel.jpg',
                                                                '9.Combo.jpg',
                                                                '10.D.Decker - V.jpg',
                                                                '11.D.Decker - H.jpg',
                                                                '12.Metel.jpeg'
                                                            ].map((imgName) => {
                                                                const isSelected = selectedCardPlaceholder === imgName;
                                                                const cleanName = imgName.replace(/\.[^/.]+$/, "").replace(/^\d+\./, "");
                                                                return (
                                                                    <Grid item xs={12} sm={6} md={4} key={imgName}>
                                                                        <Card
                                                                            onClick={() => setSelectedCardPlaceholder(imgName)}
                                                                            sx={{
                                                                                border: isSelected ? '3px solid #4b49ac' : '1px solid #e2e8f0',
                                                                                borderRadius: '12px',
                                                                                overflow: 'hidden',
                                                                                cursor: 'pointer',
                                                                                position: 'relative',
                                                                                boxShadow: isSelected ? '0 10px 15px -3px rgba(75, 73, 172, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                                                                                transition: 'all 0.3s ease',
                                                                                '&:hover': {
                                                                                    transform: 'translateY(-4px)',
                                                                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Box sx={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', overflow: 'hidden' }}>
                                                                                <img
                                                                                    src={`/assets/id_card_placeholders/${imgName}`}
                                                                                    alt={cleanName}
                                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                                                    onError={(e) => {
                                                                                        e.target.onerror = null;
                                                                                        e.target.src = "https://via.placeholder.com/200x300?text=" + encodeURIComponent(cleanName);
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            <Box sx={{ p: 1.5, textAlign: 'center', background: isSelected ? '#eaeaff' : '#fff' }}>
                                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isSelected ? '#4b49ac' : '#334155' }}>
                                                                                    {cleanName}
                                                                                </Typography>
                                                                            </Box>
                                                                            {isSelected && (
                                                                                <Box sx={{
                                                                                    position: 'absolute',
                                                                                    top: 10,
                                                                                    right: 10,
                                                                                    background: '#4b49ac',
                                                                                    color: '#fff',
                                                                                    borderRadius: '50%',
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                                }}>
                                                                                    <CheckCircleIcon fontSize="inherit" style={{ fontSize: '16px' }} />
                                                                                </Box>
                                                                            )}
                                                                        </Card>
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>
                                                    </div>
                                                )}

                                                {/* STEP 3: Front Side Fields */}
                                                {activeStepsList[activeStep] === 'Front Side Fields' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit' }}>
                                                            Configure Front Side Fields:
                                                        </Typography>

                                                        <Box sx={{ mb: 2 }}>
                                                            <TextField
                                                                select
                                                                label="Select Front Field(s)"
                                                                value={selectedFrontFieldsOptions}
                                                                onChange={e => {
                                                                    const val = e.target.value;
                                                                    setSelectedFrontFieldsOptions(typeof val === 'string' ? val.split(',') : val);
                                                                }}
                                                                SelectProps={{
                                                                    multiple: true,
                                                                    renderValue: (selected) => selected.join(', ')
                                                                }}
                                                                size="small"
                                                                fullWidth
                                                            >
                                                                {(() => {
                                                                    const isMarathi = cardConfig.designLanguage === 'Marathi';
                                                                    const isStaff = cardConfig.cardType === 'Staff';
                                                                    const options = isMarathi
                                                                        ? (isStaff ? FIELDS_MARATHI_STAFF : FIELDS_MARATHI_STUDENT)
                                                                        : (isStaff ? FIELDS_ENGLISH_STAFF : FIELDS_ENGLISH_STUDENT);
                                                                    return options.map(opt => (
                                                                        <MenuItem key={opt} value={opt}>
                                                                            <Checkbox checked={selectedFrontFieldsOptions.indexOf(opt) > -1} size="small" />
                                                                            <span style={{ fontSize: '0.9rem', marginLeft: '8px' }}>{opt}</span>
                                                                        </MenuItem>
                                                                    ));
                                                                })()}
                                                            </TextField>
                                                        </Box>

                                                        <Box sx={{ mb: 3 }}>
                                                            <Button
                                                                variant="contained"
                                                                className="primary-btn"
                                                                fullWidth
                                                                onClick={() => {
                                                                    if (selectedFrontFieldsOptions.length === 0) {
                                                                        enqueueSnackbar('Please select standard field name(s)!', { variant: 'warning' });
                                                                        return;
                                                                    }

                                                                    const newItems = selectedFrontFieldsOptions.filter(x => !frontFields.includes(x));
                                                                    if (newItems.length === 0) {
                                                                        enqueueSnackbar('All selected fields already exist!', { variant: 'warning' });
                                                                        return;
                                                                    }

                                                                    setFrontFields(prev => [...prev, ...newItems]);
                                                                    setSelectedFrontFieldsOptions([]);
                                                                }}
                                                            >
                                                                Add Selected Field(s)
                                                            </Button>
                                                        </Box>

                                                        <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />

                                                        {/* Custom Field Input Area */}
                                                        <Box sx={{ mb: 2 }}>
                                                            <TextField
                                                                label="Enter Custom Field Name"
                                                                value={frontCustomFieldInput}
                                                                onChange={e => setFrontCustomFieldInput(e.target.value)}
                                                                size="small"
                                                                fullWidth
                                                                placeholder="e.g. Bus Number"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                        <Box sx={{ mb: 3 }}>
                                                            <Button
                                                                variant="contained"
                                                                className="primary-btn"
                                                                fullWidth
                                                                onClick={() => {
                                                                    const customVal = frontCustomFieldInput.trim();
                                                                    if (!customVal) {
                                                                        enqueueSnackbar('Please enter a custom field name!', { variant: 'warning' });
                                                                        return;
                                                                    }
                                                                    if (frontFields.includes(customVal)) {
                                                                        enqueueSnackbar('Field already exists!', { variant: 'warning' });
                                                                        return;
                                                                    }
                                                                    setFrontFields(prev => [...prev, customVal]);
                                                                    setFrontCustomFieldInput('');
                                                                    enqueueSnackbar('Custom field added', { variant: 'info' });
                                                                }}
                                                            >
                                                                Add Custom Field
                                                            </Button>
                                                        </Box>

                                                        {frontFields.length > 0 && (
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
                                                                    Added Front Fields ({frontFields.length}):
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                    {frontFields.map(f => (
                                                                        <Chip
                                                                            key={f}
                                                                            label={f}
                                                                            onDelete={() => setFrontFields(prev => prev.filter(x => x !== f))}
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            style={{ margin: '4px' }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        <Divider sx={{ my: 3 }} />

                                                        {/* Authorised Sign Tag Section */}
                                                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit' }}>
                                                            Authorised Sign Tag:
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField
                                                                    select
                                                                    label="Authorised Sign Tag *"
                                                                    name="authorizedSignTag"
                                                                    value={cardConfig.authorizedSignTag || (cardConfig.designLanguage === 'Marathi' ? 'मुख्याध्यापक' : 'Principal')}
                                                                    onChange={handleCardChange}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                >
                                                                    {cardConfig.designLanguage === 'Marathi' ? (
                                                                        MARATHI_SIGN_TAGS.map(tag => (
                                                                            <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                                                                        ))
                                                                    ) : (
                                                                        ENGLISH_SIGN_TAGS.map(tag => (
                                                                            <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                                                                        ))
                                                                    )}
                                                                    <MenuItem value="Other">Other</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            {cardConfig.authorizedSignTag === 'Other' && (
                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        label="Specify Authorised Sign Tag *"
                                                                        name="customAuthorizedSignTag"
                                                                        value={cardConfig.customAuthorizedSignTag || ''}
                                                                        onChange={handleCardChange}
                                                                        fullWidth
                                                                        variant="outlined"
                                                                    />
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </div>
                                                )}

                                                {/* STEP 4: Back Side Fields */}
                                                {activeStepsList[activeStep] === 'Back Side Fields' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit' }}>
                                                            Configure Back Side Fields:
                                                        </Typography>

                                                        <Box sx={{ mb: 2 }}>
                                                            <TextField
                                                                select
                                                                label="Select Back Field(s)"
                                                                value={selectedBackFieldsOptions}
                                                                onChange={e => {
                                                                    const val = e.target.value;
                                                                    setSelectedBackFieldsOptions(typeof val === 'string' ? val.split(',') : val);
                                                                }}
                                                                SelectProps={{
                                                                    multiple: true,
                                                                    renderValue: (selected) => selected.join(', ')
                                                                }}
                                                                size="small"
                                                                fullWidth
                                                            >
                                                                {(() => {
                                                                    const isMarathi = cardConfig.designLanguage === 'Marathi';
                                                                    const isStaff = cardConfig.cardType === 'Staff';
                                                                    const options = isMarathi
                                                                        ? (isStaff ? FIELDS_MARATHI_STAFF : FIELDS_MARATHI_STUDENT)
                                                                        : (isStaff ? FIELDS_ENGLISH_STAFF : FIELDS_ENGLISH_STUDENT);
                                                                    return options.map(opt => (
                                                                        <MenuItem key={opt} value={opt}>
                                                                            <Checkbox checked={selectedBackFieldsOptions.indexOf(opt) > -1} size="small" />
                                                                            <span style={{ fontSize: '0.9rem', marginLeft: '8px' }}>{opt}</span>
                                                                        </MenuItem>
                                                                    ));
                                                                })()}
                                                            </TextField>
                                                        </Box>

                                                        <Box sx={{ mb: 3 }}>
                                                            <Button
                                                                variant="contained"
                                                                className="primary-btn"
                                                                fullWidth
                                                                onClick={() => {
                                                                    if (selectedBackFieldsOptions.length === 0) {
                                                                        enqueueSnackbar('Please select standard field name(s)!', { variant: 'warning' });
                                                                        return;
                                                                    }

                                                                    const newItems = selectedBackFieldsOptions.filter(x => !backFields.includes(x));
                                                                    if (newItems.length === 0) {
                                                                        enqueueSnackbar('All selected fields already exist!', { variant: 'warning' });
                                                                        return;
                                                                    }

                                                                    setBackFields(prev => [...prev, ...newItems]);
                                                                    setSelectedBackFieldsOptions([]);
                                                                }}
                                                            >
                                                                Add Selected Field(s)
                                                            </Button>
                                                        </Box>

                                                        <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />

                                                        {/* Custom Field Input Area */}
                                                        <Box sx={{ mb: 2 }}>
                                                            <TextField
                                                                label="Enter Custom Field Name"
                                                                value={backCustomFieldInput}
                                                                onChange={e => setBackCustomFieldInput(e.target.value)}
                                                                size="small"
                                                                fullWidth
                                                                placeholder="e.g. Address"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                        <Box sx={{ mb: 3 }}>
                                                            <Button
                                                                variant="contained"
                                                                className="primary-btn"
                                                                fullWidth
                                                                onClick={() => {
                                                                    const customVal = backCustomFieldInput.trim();
                                                                    if (!customVal) {
                                                                        enqueueSnackbar('Please enter a custom field name!', { variant: 'warning' });
                                                                        return;
                                                                    }
                                                                    if (backFields.includes(customVal)) {
                                                                        enqueueSnackbar('Field already exists!', { variant: 'warning' });
                                                                        return;
                                                                    }
                                                                    setBackFields(prev => [...prev, customVal]);
                                                                    setBackCustomFieldInput('');
                                                                    enqueueSnackbar('Custom field added', { variant: 'info' });
                                                                }}
                                                            >
                                                                Add Custom Field
                                                            </Button>
                                                        </Box>

                                                        {backFields.length > 0 && (
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
                                                                    Added Back Fields ({backFields.length}):
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                    {backFields.map(f => (
                                                                        <Chip
                                                                            key={f}
                                                                            label={f}
                                                                            onDelete={() => setBackFields(prev => prev.filter(x => x !== f))}
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            style={{ margin: '4px' }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </div>
                                                )}

                                                {/* STEP 5: Upload Assets */}
                                                {activeStepsList[activeStep] === 'Upload Assets' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <div className="upload-section-title">Upload School Assets</div>
                                                        <Grid container spacing={2} className="mb-4">
                                                            <Grid item xs={12} sm={4}>
                                                                <div className="mock-upload-box" style={{ height: '140px' }}>
                                                                    <CloudUploadIcon />
                                                                    <Typography variant="body2" className="upload-label" style={{ fontWeight: 600 }}>Organisation Logo</Typography>
                                                                    <input type="file" accept="image/*" className="upload-input" onChange={(e) => handleFileUpload('logo', e)} />
                                                                    {brandingDetails.logo && <Chip className="uploaded-chip" label={getFileNameFromUrl(brandingDetails.logo)} color="primary" variant="outlined" />}
                                                                </div>
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}>
                                                                <div className="mock-upload-box" style={{ height: '140px' }}>
                                                                    <CloudUploadIcon />
                                                                    <Typography variant="body2" className="upload-label" style={{ fontWeight: 600 }}>Authorised Signature</Typography>
                                                                    <input type="file" accept="image/*" className="upload-input" onChange={(e) => handleFileUpload('authoritySignature', e)} />
                                                                    {brandingDetails.authoritySignature && <Chip className="uploaded-chip" label={getFileNameFromUrl(brandingDetails.authoritySignature)} color="primary" variant="outlined" />}
                                                                </div>
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}>
                                                                <div className="mock-upload-box" style={{ height: '140px' }}>
                                                                    <CloudUploadIcon />
                                                                    <Typography variant="body2" className="upload-label" style={{ fontWeight: 600 }}>Any Other Logo or Image</Typography>
                                                                    <input type="file" accept="image/*" className="upload-input" onChange={(e) => handleFileUpload('stamp', e)} />
                                                                    {brandingDetails.stamp && <Chip className="uploaded-chip" label={getFileNameFromUrl(brandingDetails.stamp)} color="primary" variant="outlined" />}
                                                                </div>
                                                            </Grid>
                                                        </Grid>
                                                    </div>
                                                )}

                                                {/* STEP 6: Lanyard Config */}
                                                {activeStepsList[activeStep] === 'Lanyard Config' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <TextField
                                                                    select
                                                                    label="Lanyard Text Option *"
                                                                    value={lanyardConfig.lanyardTextOption || 'Display Brand Name'}
                                                                    onChange={e => handleLanyardChange('lanyardTextOption', e.target.value)}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                >
                                                                    <MenuItem value="Display Brand Name">Brand Name</MenuItem>
                                                                    <MenuItem value="Display Custom Text">Custom Text</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            {lanyardConfig.lanyardTextOption === 'Display Custom Text' && (
                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        label="Custom Text Box *"
                                                                        value={lanyardConfig.customText || ''}
                                                                        onChange={e => handleLanyardChange('customText', e.target.value)}
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        placeholder="Enter the custom text to display on lanyard"
                                                                    />
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </div>
                                                )}

                                                {/* STEP 8: Class & House Setup */}
                                                {activeStepsList[activeStep] === 'Class & House Setup' && (
                                                    <div className="setup-step-content animate-fade">
                                                        <Grid container spacing={3}>
                                                            {/* Column 1: Classes */}
                                                            <Grid item xs={12} md={4}>
                                                                <Card variant="outlined" sx={{ p: 3, borderRadius: '12px', borderColor: 'rgba(75, 73, 172, 0.2)', height: '100%' }}>
                                                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <SchoolIcon /> 1. Classes Setup
                                                                    </Typography>

                                                                    {cardConfig.designLanguage === 'English' && (
                                                                        <Box sx={{ mb: 2 }}>
                                                                            <TextField
                                                                                select
                                                                                label="Class Format Style"
                                                                                value={classFormatStyle}
                                                                                onChange={e => {
                                                                                    setClassFormatStyle(e.target.value);
                                                                                    setSelectedClassesOptions([]);
                                                                                }}
                                                                                size="small"
                                                                                fullWidth
                                                                            >
                                                                                <MenuItem value="Regular">Regular (e.g. 1st, 2nd)</MenuItem>
                                                                                <MenuItem value="Roman">Roman Numeral (e.g. I, II)</MenuItem>
                                                                            </TextField>
                                                                        </Box>
                                                                    )}

                                                                    <Box sx={{ mb: 2 }}>
                                                                        <TextField
                                                                            select
                                                                            label="Select Class(es)"
                                                                            value={selectedClassesOptions}
                                                                            onChange={e => {
                                                                                const val = e.target.value;
                                                                                setSelectedClassesOptions(typeof val === 'string' ? val.split(',') : val);
                                                                            }}
                                                                            SelectProps={{
                                                                                multiple: true,
                                                                                renderValue: (selected) => selected.join(', ')
                                                                            }}
                                                                            size="small"
                                                                            fullWidth
                                                                        >
                                                                            {(() => {
                                                                                const options = cardConfig.designLanguage === 'English'
                                                                                    ? (classFormatStyle === 'Roman' ? ENGLISH_CLASSES_ROMAN : ENGLISH_CLASSES_REGULAR)
                                                                                    : MARATHI_CLASSES;
                                                                                return options.map(opt => (
                                                                                    <MenuItem key={opt} value={opt}>
                                                                                        <Checkbox checked={selectedClassesOptions.indexOf(opt) > -1} size="small" />
                                                                                        <span style={{ fontSize: '0.9rem', marginLeft: '8px' }}>{opt}</span>
                                                                                    </MenuItem>
                                                                                ));
                                                                            })()}
                                                                            <MenuItem
                                                                                value="Custom"
                                                                                sx={{
                                                                                    borderTop: '1px dashed #cbd5e1',
                                                                                    mt: 1,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#4b49ac',
                                                                                    '&:hover': { background: 'rgba(75, 73, 172, 0.08)' }
                                                                                }}
                                                                            >
                                                                                <Checkbox
                                                                                    checked={selectedClassesOptions.indexOf('Custom') > -1}
                                                                                    size="small"
                                                                                    sx={{ color: '#4b49ac', '&.Mui-checked': { color: '#4b49ac' } }}
                                                                                />
                                                                                <span style={{ fontSize: '0.9rem', marginLeft: '8px', fontWeight: 700 }}>Custom (Add manually)</span>
                                                                            </MenuItem>
                                                                        </TextField>
                                                                    </Box>

                                                                    {selectedClassesOptions.includes('Custom') && (
                                                                        <Box sx={{ mb: 2 }}>
                                                                            <TextField
                                                                                label="Enter Custom Class Name"
                                                                                value={customClassValue}
                                                                                onChange={e => setCustomClassValue(e.target.value)}
                                                                                size="small"
                                                                                fullWidth
                                                                                placeholder="e.g. Play Group Special"
                                                                                helperText="Type your custom class name"
                                                                                sx={{
                                                                                    background: 'rgba(75, 73, 172, 0.02)',
                                                                                    '& .MuiOutlinedInput-root': {
                                                                                        '& fieldset': { borderColor: '#4b49ac', borderStyle: 'dashed', borderWidth: '1.5px' }
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    )}

                                                                    <Box sx={{ mb: 3 }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            className="primary-btn"
                                                                            fullWidth
                                                                            onClick={() => {
                                                                                const toAdd = selectedClassesOptions.filter(x => x !== 'Custom');
                                                                                if (selectedClassesOptions.includes('Custom')) {
                                                                                    const customVal = customClassValue.trim();
                                                                                    if (customVal && !toAdd.includes(customVal)) {
                                                                                        toAdd.push(customVal);
                                                                                    }
                                                                                }

                                                                                if (toAdd.length === 0) {
                                                                                    enqueueSnackbar('Please select or enter class name(s)!', { variant: 'warning' });
                                                                                    return;
                                                                                }

                                                                                const newItems = toAdd.filter(x => !classesList.includes(x));
                                                                                if (newItems.length === 0) {
                                                                                    enqueueSnackbar('All selected classes already exist!', { variant: 'warning' });
                                                                                    return;
                                                                                }

                                                                                setClassesList(prev => [...prev, ...newItems]);
                                                                                setSelectedClassesOptions([]);
                                                                                setCustomClassValue('');
                                                                            }}
                                                                        >
                                                                            Add Class(es)
                                                                        </Button>
                                                                    </Box>

                                                                    {classesList.length === 0 ? (
                                                                        <Box sx={{ p: 3, textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                                                            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                                                                                No classes added.
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '200px', overflowY: 'auto' }}>
                                                                            {classesList.map(c => (
                                                                                <Chip
                                                                                    key={c}
                                                                                    label={c}
                                                                                    color="primary"
                                                                                    onDelete={() => setClassesList(prev => prev.filter(x => x !== c))}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Card>
                                                            </Grid>

                                                            {/* Column 2: Divisions */}
                                                            <Grid item xs={12} md={4}>
                                                                <Card variant="outlined" sx={{ p: 3, borderRadius: '12px', borderColor: 'rgba(75, 73, 172, 0.2)', height: '100%' }}>
                                                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <SchoolIcon /> 2. Divisions Setup
                                                                    </Typography>

                                                                    <Box sx={{ mb: 2 }}>
                                                                        <TextField
                                                                            select
                                                                            label="Select Division(s)"
                                                                            value={selectedDivisionsOptions}
                                                                            onChange={e => {
                                                                                const val = e.target.value;
                                                                                setSelectedDivisionsOptions(typeof val === 'string' ? val.split(',') : val);
                                                                            }}
                                                                            SelectProps={{
                                                                                multiple: true,
                                                                                renderValue: (selected) => selected.join(', ')
                                                                            }}
                                                                            size="small"
                                                                            fullWidth
                                                                        >
                                                                            {(() => {
                                                                                const options = cardConfig.designLanguage === 'English'
                                                                                    ? ENGLISH_DIVISIONS
                                                                                    : MARATHI_DIVISIONS;
                                                                                return options.map(opt => (
                                                                                    <MenuItem key={opt} value={opt}>
                                                                                        <Checkbox checked={selectedDivisionsOptions.indexOf(opt) > -1} size="small" />
                                                                                        <span style={{ fontSize: '0.9rem', marginLeft: '8px' }}>{opt}</span>
                                                                                    </MenuItem>
                                                                                ));
                                                                            })()}
                                                                            <MenuItem
                                                                                value="Custom"
                                                                                sx={{
                                                                                    borderTop: '1px dashed #cbd5e1',
                                                                                    mt: 1,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#4b49ac',
                                                                                    '&:hover': { background: 'rgba(75, 73, 172, 0.08)' }
                                                                                }}
                                                                            >
                                                                                <Checkbox
                                                                                    checked={selectedDivisionsOptions.indexOf('Custom') > -1}
                                                                                    size="small"
                                                                                    sx={{ color: '#4b49ac', '&.Mui-checked': { color: '#4b49ac' } }}
                                                                                />
                                                                                <span style={{ fontSize: '0.9rem', marginLeft: '8px', fontWeight: 700 }}>Custom (Add manually)</span>
                                                                            </MenuItem>
                                                                        </TextField>
                                                                    </Box>

                                                                    {selectedDivisionsOptions.includes('Custom') && (
                                                                        <Box sx={{ mb: 2 }}>
                                                                            <TextField
                                                                                label="Enter Custom Division Name"
                                                                                value={customDivisionValue}
                                                                                onChange={e => setCustomDivisionValue(e.target.value)}
                                                                                size="small"
                                                                                fullWidth
                                                                                placeholder="e.g. F"
                                                                                helperText="Type your custom division name"
                                                                                sx={{
                                                                                    background: 'rgba(75, 73, 172, 0.02)',
                                                                                    '& .MuiOutlinedInput-root': {
                                                                                        '& fieldset': { borderColor: '#4b49ac', borderStyle: 'dashed', borderWidth: '1.5px' }
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    )}

                                                                    <Box sx={{ mb: 3 }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            className="primary-btn"
                                                                            fullWidth
                                                                            onClick={() => {
                                                                                const toAdd = selectedDivisionsOptions.filter(x => x !== 'Custom');
                                                                                if (selectedDivisionsOptions.includes('Custom')) {
                                                                                    const customVal = customDivisionValue.trim();
                                                                                    if (customVal && !toAdd.includes(customVal)) {
                                                                                        toAdd.push(customVal);
                                                                                    }
                                                                                }

                                                                                if (toAdd.length === 0) {
                                                                                    enqueueSnackbar('Please select or enter division name(s)!', { variant: 'warning' });
                                                                                    return;
                                                                                }

                                                                                let processed = toAdd;
                                                                                if (cardConfig.designLanguage === 'English') {
                                                                                    processed = toAdd.map(x => x.toUpperCase());
                                                                                }

                                                                                const newItems = processed.filter(x => !divisionsList.includes(x));
                                                                                if (newItems.length === 0) {
                                                                                    enqueueSnackbar('All selected divisions already exist!', { variant: 'warning' });
                                                                                    return;
                                                                                }

                                                                                setDivisionsList(prev => [...prev, ...newItems]);
                                                                                setSelectedDivisionsOptions([]);
                                                                                setCustomDivisionValue('');
                                                                            }}
                                                                        >
                                                                            Add Division(s)
                                                                        </Button>
                                                                    </Box>

                                                                    {divisionsList.length === 0 ? (
                                                                        <Box sx={{ p: 3, textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                                                            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                                                                                No divisions added.
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '200px', overflowY: 'auto' }}>
                                                                            {divisionsList.map(d => (
                                                                                <Chip
                                                                                    key={d}
                                                                                    label={d}
                                                                                    color="primary"
                                                                                    variant="outlined"
                                                                                    onDelete={() => setDivisionsList(prev => prev.filter(x => x !== d))}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Card>
                                                            </Grid>

                                                            {/* Column 3: Houses */}
                                                            <Grid item xs={12} md={4}>
                                                                <Card variant="outlined" sx={{ p: 3, borderRadius: '12px', borderColor: 'rgba(75, 73, 172, 0.2)', height: '100%' }}>
                                                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#4b49ac', fontFamily: 'Outfit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <PaletteIcon /> 3. Houses Setup
                                                                    </Typography>

                                                                    <Box sx={{ mb: 2 }}>
                                                                        <TextField
                                                                            select
                                                                            label="Select House(s)"
                                                                            value={selectedHousesOptions}
                                                                            onChange={e => {
                                                                                const val = e.target.value;
                                                                                setSelectedHousesOptions(typeof val === 'string' ? val.split(',') : val);
                                                                            }}
                                                                            SelectProps={{
                                                                                multiple: true,
                                                                                renderValue: (selected) => selected.join(', ')
                                                                            }}
                                                                            size="small"
                                                                            fullWidth
                                                                        >
                                                                            {HOUSE_OPTIONS.map(opt => (
                                                                                <MenuItem key={opt} value={opt}>
                                                                                    <Checkbox checked={selectedHousesOptions.indexOf(opt) > -1} size="small" />
                                                                                    <span style={{ fontSize: '0.9rem', marginLeft: '8px' }}>{opt}</span>
                                                                                </MenuItem>
                                                                            ))}
                                                                            <MenuItem
                                                                                value="Custom"
                                                                                sx={{
                                                                                    borderTop: '1px dashed #cbd5e1',
                                                                                    mt: 1,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#4b49ac',
                                                                                    '&:hover': { background: 'rgba(75, 73, 172, 0.08)' }
                                                                                }}
                                                                            >
                                                                                <Checkbox
                                                                                    checked={selectedHousesOptions.indexOf('Custom') > -1}
                                                                                    size="small"
                                                                                    sx={{ color: '#4b49ac', '&.Mui-checked': { color: '#4b49ac' } }}
                                                                                />
                                                                                <span style={{ fontSize: '0.9rem', marginLeft: '8px', fontWeight: 700 }}>Custom (Add manually)</span>
                                                                            </MenuItem>
                                                                        </TextField>
                                                                    </Box>

                                                                    {selectedHousesOptions.includes('Custom') && (
                                                                        <Box sx={{ mb: 2 }}>
                                                                            <TextField
                                                                                label="Enter Custom House Name"
                                                                                value={customHouseValue}
                                                                                onChange={e => setCustomHouseValue(e.target.value)}
                                                                                size="small"
                                                                                fullWidth
                                                                                placeholder="e.g. Gold"
                                                                                helperText="Type your custom house name"
                                                                                sx={{
                                                                                    background: 'rgba(75, 73, 172, 0.02)',
                                                                                    '& .MuiOutlinedInput-root': {
                                                                                        '& fieldset': { borderColor: '#4b49ac', borderStyle: 'dashed', borderWidth: '1.5px' }
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    )}

                                                                    <Box sx={{ mb: 3 }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            className="primary-btn"
                                                                            fullWidth
                                                                            onClick={() => {
                                                                                const toAdd = selectedHousesOptions.filter(x => x !== 'Custom');
                                                                                if (selectedHousesOptions.includes('Custom')) {
                                                                                    const customVal = customHouseValue.trim();
                                                                                    if (customVal && !toAdd.includes(customVal)) {
                                                                                        toAdd.push(customVal);
                                                                                    }
                                                                                }

                                                                                if (toAdd.length === 0) {
                                                                                    enqueueSnackbar('Please select or enter house name(s)!', { variant: 'warning' });
                                                                                    return;
                                                                                }

                                                                                const newItems = toAdd.filter(x => !housesList.includes(x));
                                                                                if (newItems.length === 0) {
                                                                                    enqueueSnackbar('All selected houses already exist!', { variant: 'warning' });
                                                                                    return;
                                                                                }

                                                                                setHousesList(prev => [...prev, ...newItems]);
                                                                                setSelectedHousesOptions([]);
                                                                                setCustomHouseValue('');
                                                                            }}
                                                                        >
                                                                            Add House(s)
                                                                        </Button>
                                                                    </Box>

                                                                    {housesList.length === 0 ? (
                                                                        <Box sx={{ p: 3, textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                                                            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                                                                                No houses added.
                                                                            </Typography>
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '200px', overflowY: 'auto' }}>
                                                                            {housesList.map(h => (
                                                                                <Chip
                                                                                    key={h}
                                                                                    label={h}
                                                                                    color="secondary"
                                                                                    onDelete={() => setHousesList(prev => prev.filter(x => x !== h))}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Card>
                                                            </Grid>
                                                        </Grid>
                                                    </div>
                                                )}
                                            </div>

                                            <Divider className="my-3" />

                                            {/* Stepper Navigation */}
                                            <div className="stepper-actions-bar">
                                                <Button variant="outlined" onClick={handleBack} className="secondary-btn">Back</Button>
                                                {activeStep < activeStepsList.length - 1 ? (
                                                    <Button variant="contained" onClick={handleNext} className="primary-btn">Next Step</Button>
                                                ) : (
                                                    <Button variant="contained" onClick={handleSubmitForm} className="success-btn">Submit Setup</Button>
                                                )}
                                            </div>
                                        </Card>
                                    </div>

                                </div>
                            )}
                        </div>
                    )}

                    {/* 2. ADMIN VIEW MODE (Review Submissions and Copy Prompts) */}
                    {isAdminMode && (
                        <div className="admin-dashboard-mode animate-fade" style={{ padding: '10px' }}>
                            {selectedSchoolConfig ? (
                                <Box className="animate-fade">
                                    {/* Page Header with back button */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                                        <Button
                                            variant="text"
                                            color="primary"
                                            onClick={() => {
                                                setSelectedSchoolConfig(null);
                                                setSelectedSchoolIndex(-1);
                                            }}
                                            startIcon={<ArrowBackIcon />}
                                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontFamily: 'Outfit', p: 0 }}
                                        >
                                            Back to Submissions List
                                        </Button>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <Typography variant="h5" style={{ fontWeight: 800, color: '#1e1b4b', fontFamily: 'Outfit' }}>
                                                    {selectedSchoolConfig.organizationDetails.name}
                                                </Typography>
                                                <Chip label={selectedSchoolConfig.cardConfiguration.cardType} color="primary" size="small" style={{ fontWeight: 600, fontFamily: 'Outfit' }} />
                                            </Box>

                                            {/* Tabs control */}
                                            <Box sx={{ background: '#fafbfe', borderRadius: '10px', p: 0.5, border: '1px solid #eaeaff' }}>
                                                <Tabs
                                                    value={adminTabValue}
                                                    onChange={(_, val) => setAdminTabValue(val)}
                                                    textColor="primary"
                                                    indicatorColor="primary"
                                                    sx={{ minHeight: '36px' }}
                                                >
                                                    <Tab label="School Details" sx={{ textTransform: 'none', fontWeight: 600, minHeight: '36px', px: 2 }} />
                                                    <Tab label="Compiled AI Prompt" sx={{ textTransform: 'none', fontWeight: 600, minHeight: '36px', px: 2 }} />
                                                    <Tab label="JSON Schema" sx={{ textTransform: 'none', fontWeight: 600, minHeight: '36px', px: 2 }} />
                                                </Tabs>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Card className="elevation-2" style={{ borderRadius: '16px', padding: '24px', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                        {/* Tab 1: Details */}
                                        {adminTabValue === 0 && (
                                            <div className="animate-fade">
                                                <Grid container spacing={3}>
                                                    {/* ID Card Configuration */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>1. ID Card Configuration</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                            <strong>Design Language:</strong> {selectedSchoolConfig.cardConfiguration.designLanguage || 'English'}
                                                            <br />
                                                            <strong>Card Type:</strong> {selectedSchoolConfig.cardConfiguration.cardType || 'Student'}
                                                            <br />
                                                            <strong>Orientation:</strong> {selectedSchoolConfig.cardConfiguration.orientation || 'Vertical'}
                                                            <br />
                                                            <strong>Card Layout:</strong> {selectedSchoolConfig.cardConfiguration.layoutType || 'Both Side'}
                                                            <br />
                                                            <strong>Generate Lanyard:</strong> {selectedSchoolConfig.cardConfiguration.generateLanyard || 'Yes'}
                                                            <br />
                                                            <strong>Design Colour:</strong> {selectedSchoolConfig.cardConfiguration.designColor || 'Royal Blue'}
                                                            <br />
                                                            <strong>Selected ID Card Placeholder:</strong> {selectedSchoolConfig.idCardDesignPlaceholder || 'N/A'}
                                                            {selectedSchoolConfig.idCardDesignPlaceholder && (
                                                                <Box sx={{ mt: 1, maxWidth: '150px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                                                    <img
                                                                        src={`/assets/id_card_placeholders/${selectedSchoolConfig.idCardDesignPlaceholder}`}
                                                                        alt="Selected Template"
                                                                        style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = "https://via.placeholder.com/150x220?text=" + encodeURIComponent(selectedSchoolConfig.idCardDesignPlaceholder);
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}
                                                            <div style={{ display: 'flex', gap: '15px', marginTop: '8px', flexWrap: 'wrap' }}>
                                                                {selectedSchoolConfig.cardConfiguration.designColor === 'Custom' ? (
                                                                    <span className="color-swatch-item">Custom: <span style={{ background: selectedSchoolConfig.cardConfiguration.customColor || '#438CAC' }} className="color-swatch-circle"></span> {selectedSchoolConfig.cardConfiguration.customColor || '#438CAC'}</span>
                                                                ) : (
                                                                    <span className="color-swatch-item">Theme Color: {selectedSchoolConfig.cardConfiguration.designColor || 'Royal Blue'}</span>
                                                                )}
                                                                {selectedSchoolConfig.brandingDetails?.primaryColor && (
                                                                    <span className="color-swatch-item">Primary: <span style={{ background: selectedSchoolConfig.brandingDetails.primaryColor }} className="color-swatch-circle"></span> {selectedSchoolConfig.brandingDetails.primaryColor}</span>
                                                                )}
                                                                {selectedSchoolConfig.brandingDetails?.secondaryColor && (
                                                                    <span className="color-swatch-item">Secondary: <span style={{ background: selectedSchoolConfig.brandingDetails.secondaryColor }} className="color-swatch-circle"></span> {selectedSchoolConfig.brandingDetails.secondaryColor}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Grid>

                                                    {/* Organisation Details */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>2. Organisation Details</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                            <strong>Organisation Type:</strong> {selectedSchoolConfig.organizationDetails.organisationType || selectedSchoolConfig.organizationDetails.type || 'Sanstha Name'}
                                                            <br />
                                                            <strong>Organisation Identifier/Value:</strong> {selectedSchoolConfig.organizationDetails.organisationValue || selectedSchoolConfig.organizationDetails.additionalInfo || 'N/A'}
                                                            <br />
                                                            <strong>Brand Name:</strong> {selectedSchoolConfig.organizationDetails.name}
                                                            <br />
                                                            <strong>Address:</strong> {selectedSchoolConfig.organizationDetails.address} {selectedSchoolConfig.organizationDetails.city ? `, ${selectedSchoolConfig.organizationDetails.city}` : ''} {selectedSchoolConfig.organizationDetails.state ? `, ${selectedSchoolConfig.organizationDetails.state}` : ''}
                                                            <br />
                                                            <strong>Contact No:</strong> {selectedSchoolConfig.organizationDetails.contactNumber}
                                                            <br />
                                                            <strong>Email ID:</strong> {selectedSchoolConfig.organizationDetails.email || 'N/A'}
                                                            <br />
                                                            <strong>Website:</strong> {selectedSchoolConfig.organizationDetails.website ? <a href={selectedSchoolConfig.organizationDetails.website} target="_blank" rel="noopener noreferrer">{selectedSchoolConfig.organizationDetails.website}</a> : 'N/A'}
                                                        </div>
                                                    </Grid>

                                                    {/* Front & Back Side Fields */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>3 & 4. Field Placements</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                            <strong>Authorised Sign Tag:</strong> {selectedSchoolConfig.brandingDetails.authorisedSignTag || selectedSchoolConfig.brandingDetails.fontPreference || (selectedSchoolConfig.cardConfiguration.authorizedSignTag === 'Other' ? (selectedSchoolConfig.cardConfiguration.customAuthorizedSignTag || 'Authorized Signatory') : (selectedSchoolConfig.cardConfiguration.authorizedSignTag || 'Principal'))}
                                                            <br />
                                                            <div style={{ marginTop: '8px' }}></div>
                                                            <strong>Front Custom Fields:</strong> {(!selectedSchoolConfig.frontCustomFields || selectedSchoolConfig.frontCustomFields.length === 0) ? 'None selected' : selectedSchoolConfig.frontCustomFields.join(', ')}
                                                            {selectedSchoolConfig.frontFields && selectedSchoolConfig.frontFields.length > 0 && (
                                                                <div><strong>Front Checkbox Fields (Legacy):</strong> {selectedSchoolConfig.frontFields.join(', ')}</div>
                                                            )}

                                                            {(selectedSchoolConfig.cardConfiguration.layoutType !== 'One Side' && selectedSchoolConfig.cardConfiguration.layoutType !== 'Single Sided') && (
                                                                <>
                                                                    <div style={{ marginTop: '8px' }}></div>
                                                                    <strong>Back Custom Fields:</strong> {(!selectedSchoolConfig.backCustomFields || selectedSchoolConfig.backCustomFields.length === 0) ? 'None selected' : selectedSchoolConfig.backCustomFields.join(', ')}
                                                                    {selectedSchoolConfig.backFields && selectedSchoolConfig.backFields.length > 0 && (
                                                                        <div><strong>Back Checkbox Fields (Legacy):</strong> {selectedSchoolConfig.backFields.join(', ')}</div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </Grid>

                                                    {/* Lanyard Specifications */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>6. Lanyard Specifications</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                            {selectedSchoolConfig.lanyardConfiguration ? (
                                                                <>
                                                                    <strong>Text Option:</strong> {getLanyardTextOptionDisplay(selectedSchoolConfig)}
                                                                    <br /><strong>Lanyard Text:</strong> {getLanyardTextDisplay(selectedSchoolConfig)}
                                                                    {selectedSchoolConfig.lanyardConfiguration.logo && (
                                                                        <>
                                                                            <br /><strong>Lanyard Logo:</strong> <a href={selectedSchoolConfig.lanyardConfiguration.logo} target="_blank" rel="noopener noreferrer">View Logo</a>
                                                                        </>
                                                                    )}
                                                                    {(selectedSchoolConfig.lanyardConfiguration.primaryColor || selectedSchoolConfig.lanyardConfiguration.secondaryColor) && (
                                                                        <div style={{ display: 'flex', gap: '15px', marginTop: '8px' }}>
                                                                            <span className="color-swatch-item">Primary: <span style={{ background: selectedSchoolConfig.lanyardConfiguration.primaryColor }} className="color-swatch-circle"></span> {selectedSchoolConfig.lanyardConfiguration.primaryColor}</span>
                                                                            <span className="color-swatch-item">Secondary: <span style={{ background: selectedSchoolConfig.lanyardConfiguration.secondaryColor }} className="color-swatch-circle"></span> {selectedSchoolConfig.lanyardConfiguration.secondaryColor}</span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span style={{ fontStyle: 'italic', color: '#888' }}>Lanyard is not enabled/required.</span>
                                                            )}
                                                        </div>
                                                    </Grid>

                                                    {/* Assets Details */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>5. Uploaded School Assets</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                            <strong>School Logo:</strong> {selectedSchoolConfig.brandingDetails.logo ? <a href={selectedSchoolConfig.brandingDetails.logo} target="_blank" rel="noopener noreferrer">View Logo Image</a> : 'Not Uploaded'}
                                                            <br />
                                                            <strong>Authorised Signature:</strong> {selectedSchoolConfig.brandingDetails.authoritySignature ? <a href={selectedSchoolConfig.brandingDetails.authoritySignature} target="_blank" rel="noopener noreferrer">View Signature Image</a> : 'Not Uploaded'}
                                                            <br />
                                                            <strong>Any Other Logo or Image:</strong> {(selectedSchoolConfig.brandingDetails.stamp || selectedSchoolConfig.brandingDetails.otherLogo) ? <a href={selectedSchoolConfig.brandingDetails.stamp || selectedSchoolConfig.brandingDetails.otherLogo} target="_blank" rel="noopener noreferrer">View Image</a> : 'Not Uploaded'}

                                                            {selectedSchoolConfig.brandingDetails.principalSignature && (
                                                                <>
                                                                    <br /><strong>Principal Signature (Legacy):</strong> <a href={selectedSchoolConfig.brandingDetails.principalSignature} target="_blank" rel="noopener noreferrer">View</a>
                                                                </>
                                                            )}
                                                        </div>
                                                    </Grid>

                                                    {/* Academic Setup (Classes, Divisions & Houses) */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>Academic Setup</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.8 }}>
                                                            <div style={{ marginBottom: '8px' }}>
                                                                <strong>Classes:</strong> <span style={{ marginLeft: '5px' }}>{
                                                                    (!selectedSchoolConfig.classes || selectedSchoolConfig.classes.length === 0)
                                                                        ? 'None configured.'
                                                                        : (Array.isArray(selectedSchoolConfig.classes)
                                                                            ? selectedSchoolConfig.classes.join(', ')
                                                                            : String(selectedSchoolConfig.classes))
                                                                }</span>
                                                            </div>

                                                            <div style={{ marginBottom: '8px' }}>
                                                                <strong>Divisions:</strong> <span style={{ marginLeft: '5px' }}>{
                                                                    (!selectedSchoolConfig.divisions || selectedSchoolConfig.divisions.length === 0)
                                                                        ? 'None configured.'
                                                                        : (Array.isArray(selectedSchoolConfig.divisions)
                                                                            ? selectedSchoolConfig.divisions.join(', ')
                                                                            : String(selectedSchoolConfig.divisions))
                                                                }</span>
                                                            </div>

                                                            <div>
                                                                <strong>Houses:</strong> <span style={{ marginLeft: '5px' }}>{
                                                                    (!selectedSchoolConfig.houses || selectedSchoolConfig.houses.length === 0)
                                                                        ? 'None configured.'
                                                                        : (Array.isArray(selectedSchoolConfig.houses)
                                                                            ? selectedSchoolConfig.houses.join(', ')
                                                                            : String(selectedSchoolConfig.houses))
                                                                }</span>
                                                            </div>
                                                        </div>
                                                    </Grid>

                                                    {/* Vendor Details */}
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#4b49ac', borderBottom: '1px solid rgba(75, 73, 172, 0.1)', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Outfit' }}>7. Vendor Details</Typography>
                                                        <div style={{ padding: '8px 0', fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                                            {(() => {
                                                                const vendor = selectedSchoolConfig.vendor || selectedSchoolConfig.vendor_id || selectedSchoolConfig.vendorId;
                                                                if (vendor) {
                                                                    if (typeof vendor === 'object') {
                                                                        return (
                                                                            <>
                                                                                <strong>Vendor Name:</strong> {vendor.full_name || vendor.name || vendor.username || 'N/A'}
                                                                                <br />
                                                                                <strong>Contact No:</strong> {vendor.phone_no || 'N/A'}
                                                                                <br />
                                                                                <strong>Email ID:</strong> {vendor.email || 'N/A'}
                                                                            </>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <>
                                                                            <strong>Vendor ID:</strong> {String(vendor)}
                                                                        </>
                                                                    );
                                                                }
                                                                return <span style={{ fontStyle: 'italic', color: '#888' }}>Direct submission (No Vendor).</span>;
                                                            })()}
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        )}

                                        {/* Tab 2: Compiled AI Prompt */}
                                        {adminTabValue === 1 && (
                                            <div className="animate-fade">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, gap: 2, borderBottom: '1px solid #eaeaff', pb: 2 }}>
                                                    <Tabs
                                                        value={promptSideTab}
                                                        onChange={(_, val) => setPromptSideTab(val)}
                                                        indicatorColor="primary"
                                                        textColor="primary"
                                                        sx={{ minHeight: '36px' }}
                                                    >
                                                        <Tab label="Front Side" sx={{ textTransform: 'none', fontWeight: 600, minHeight: '36px', px: 2 }} />
                                                        <Tab label="Back Side" sx={{ textTransform: 'none', fontWeight: 600, minHeight: '36px', px: 2 }} />
                                                        <Tab label="Lanyard" sx={{ textTransform: 'none', fontWeight: 600, minHeight: '36px', px: 2 }} />
                                                    </Tabs>
                                                </Box>

                                                {/* Render prompt side views */}
                                                {promptSideTab === 0 && renderPromptEditorAndPreview('front')}
                                                {promptSideTab === 1 && renderPromptEditorAndPreview('back')}
                                                {promptSideTab === 2 && (
                                                    selectedSchoolConfig.cardConfiguration?.generateLanyard !== 'Yes' ? (
                                                        <Box sx={{ p: 4, textAlign: 'center', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                                            <Typography variant="body1" color="textSecondary" style={{ fontStyle: 'italic' }}>
                                                                Lanyard design is disabled or not required for this card configuration.
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        renderPromptEditorAndPreview('lanyard')
                                                    )
                                                )}
                                            </div>
                                        )}

                                        {/* Tab 3: JSON Payload */}
                                        {adminTabValue === 2 && (
                                            <div className="animate-fade">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                                    <Button
                                                        variant="contained"
                                                        className="primary-btn"
                                                        onClick={handleCopyToClipboard}
                                                        startIcon={<ContentCopyIcon />}
                                                    >
                                                        Copy JSON Payload
                                                    </Button>
                                                </Box>
                                                <div className="payload-card" style={{ border: 'none', borderRadius: '12px' }}>
                                                    <div className="payload-body">
                                                        <pre>{JSON.stringify(selectedSchoolConfig, null, 2)}</pre>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                </Box>
                            ) : (
                                <>
                                    {/* Search & Header Row */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <AdminPanelSettingsIcon style={{ color: '#4b49ac', fontSize: '1.8rem' }} />
                                            <Typography variant="h5" style={{ fontWeight: 700, color: '#1e1b4b', fontFamily: 'Outfit' }}>
                                                Submitted School Configurations
                                            </Typography>
                                        </div>

                                        <TextField
                                            size="small"
                                            placeholder="Search by school name..."
                                            value={adminSearchQuery}
                                            onChange={(e) => {
                                                setAdminSearchQuery(e.target.value);
                                                setPage(0);
                                            }}
                                            sx={{ minWidth: '300px' }}
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    {/* List Table container */}
                                    <TableContainer component={Paper} className="elevation-2" style={{ borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.8)', overflow: 'hidden' }}>
                                        <Table>
                                            <TableHead style={{ background: '#f8fafc' }}>
                                                <TableRow>
                                                    <TableCell style={{ fontWeight: 700, fontFamily: 'Outfit', color: '#475569' }}>School Name</TableCell>
                                                    <TableCell style={{ fontWeight: 700, fontFamily: 'Outfit', color: '#475569' }}>Card Type</TableCell>
                                                    <TableCell style={{ fontWeight: 700, fontFamily: 'Outfit', color: '#475569' }}>Orientation</TableCell>
                                                    <TableCell style={{ fontWeight: 700, fontFamily: 'Outfit', color: '#475569' }}>Vendor Details</TableCell>
                                                    <TableCell style={{ fontWeight: 700, fontFamily: 'Outfit', color: '#475569' }}>Submitted Date</TableCell>
                                                    <TableCell align="center" style={{ fontWeight: 700, fontFamily: 'Outfit', color: '#475569' }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {isLoading ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" style={{ padding: '40px' }}>
                                                            <CircularProgress size={30} />
                                                        </TableCell>
                                                    </TableRow>
                                                ) : submissions.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" style={{ padding: '40px', color: '#888', fontStyle: 'italic' }}>
                                                            {adminSearchQuery.trim() !== ''
                                                                ? `No configurations found matching "${adminSearchQuery}"`
                                                                : 'No configurations submitted yet. Use the public setup link to register.'
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    submissions.map((sub, index) => {
                                                        const originalIdx = page * rowsPerPage + index;
                                                        const idToUse = sub.id || originalIdx;
                                                        return (
                                                            <TableRow key={idToUse} hover style={{ cursor: 'pointer' }} onClick={() => handleOpenDetails(sub, idToUse)}>
                                                                <TableCell style={{ fontWeight: 600, color: '#1e1b4b', fontFamily: 'Plus Jakarta Sans' }}>
                                                                    {sub.organizationDetails?.name || 'N/A'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip label={sub.cardConfiguration?.cardType || 'N/A'} color="primary" size="small" style={{ fontWeight: 600 }} />
                                                                </TableCell>
                                                                <TableCell>{sub.cardConfiguration?.orientation || 'N/A'}</TableCell>
                                                                <TableCell style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                                                    {(() => {
                                                                        const vendor = sub.vendor;
                                                                        if (vendor && typeof vendor === 'object') {
                                                                            return (
                                                                                <div>
                                                                                    <Typography variant="body2" style={{ fontWeight: 600, color: '#334155', margin: 0, fontSize: '0.85rem' }}>
                                                                                        {vendor.full_name || vendor.name || vendor.username || 'N/A'}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" style={{ color: '#64748b', display: 'block', fontSize: '0.75rem' }}>
                                                                                        {vendor.phone_no || vendor.email || 'No Contact'}
                                                                                    </Typography>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>Direct/No Vendor</span>;
                                                                    })()}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {sub.dateSubmitted ? (() => {
                                                                        const d = new Date(sub.dateSubmitted);
                                                                        const day = String(d.getDate()).padStart(2, '0');
                                                                        const month = String(d.getMonth() + 1).padStart(2, '0');
                                                                        const year = d.getFullYear();
                                                                        return `${day}-${month}-${year}`;
                                                                    })() : 'N/A'}
                                                                </TableCell>
                                                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                                        <Tooltip title="View Details & Prompts">
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                className="secondary-btn"
                                                                                onClick={() => handleOpenDetails(sub, idToUse)}
                                                                                sx={{ py: 0.5, px: 2 }}
                                                                            >
                                                                                View/Edit Details
                                                                            </Button>
                                                                        </Tooltip>
                                                                        <Tooltip title="Delete Entry">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={(e) => handleDeleteSubmission(idToUse, e)}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25]}
                                        component="div"
                                        count={totalCount}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(_, newPage) => setPage(newPage)}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        sx={{ mt: 1 }}
                                    />
                                </>
                            )}
                        </div>
                    )}

                </div>

                <Dialog
                    open={openResetDialog}
                    onClose={() => setOpenResetDialog(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: '16px',
                            padding: '8px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 800, color: '#d32f2f', fontFamily: 'Outfit', display: 'flex', alignItems: 'center', gap: 1 }}>
                        Reset Prompt Template
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
                            Are you sure you want to delete your custom changes and reset the <strong>{resetSide}</strong> template to the default prompt?
                        </Typography>
                        <Typography sx={{ mt: 1.5, color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                            This action will overwrite your currently edited template in this session and cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ padding: '8px 24px 16px 24px', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setOpenResetDialog(false)}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: '#cbd5e1',
                                color: '#64748b',
                                '&:hover': {
                                    borderColor: '#94a3b8',
                                    background: '#f8fafc'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmReset}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Yes, Reset Template
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openDeleteConfirmDialog}
                    onClose={() => setOpenDeleteConfirmDialog(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: '16px',
                            padding: '8px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 800, color: '#d32f2f', fontFamily: 'Outfit', display: 'flex', alignItems: 'center', gap: 1 }}>
                        Delete School Configuration
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
                            Are you sure you want to delete this configuration?
                        </Typography>
                        <Typography sx={{ mt: 1.5, color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                            This action will permanently delete all metadata and uploaded assets associated with this setup. This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ padding: '8px 24px 16px 24px', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setOpenDeleteConfirmDialog(false)}
                            disabled={isDeleting}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: '#cbd5e1',
                                color: '#64748b',
                                '&:hover': {
                                    borderColor: '#94a3b8',
                                    background: '#f8fafc'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={isDeleting}
                            onClick={confirmDeleteSubmission}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </StandardPageWrapper>
        </div>
    );
}

export default SchoolSetup;
